import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { NavigationHeader } from '@/components/NavigationHeader';
import { auth, db } from '@/firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc as fsDoc, setDoc as fsSetDoc } from 'firebase/firestore';
import { sendPasswordResetEmail, getIdTokenResult } from 'firebase/auth';
import { listInstitutions, addInstitution, updateInstitution, deleteInstitution, listCouncilors, addCouncilor, getSettings, setSettings, updateCouncilor, listDescriptionTemplates, addDescriptionTemplate, updateDescriptionTemplate, deleteDescriptionTemplate, usersCol, listAmendments, amendmentsCol, deleteCouncilor, logAdminAction, getUserInstitutionId, setUserInstitution, logAmendmentHistory, updateAmendment, getUserRole, type Institution, type Councilor, type DescriptionTemplate, type Amendment } from '@/services/firestore';
import { onSnapshot } from 'firebase/firestore';
import { adminGet, adminPost } from '@/services/admin-api';
// import { getDocs, query, where } from 'firebase/firestore';
import { Checkbox } from '@/components/ui/checkbox';
import ApiTokens from './ApiTokens';
import { todayIsoDateCuiaba, formatDateTimeLabel, isNowPastScheduleCuiaba, recomputeGlobalSequentialNumbersByCouncilor, councilorCodeMap, buildIdentification } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

// ===== Utilitário: geração de CSV para emendas (função pura, exportada para testes) =====
export function generateAmendmentsCsv(
  rows: Array<any>,
  selectedKeys: string[],
  opts: { instMap: Map<string, Institution>; counMap: Map<string, Councilor> }
) {
  const AVAILABLE_CSV_COLUMNS: Array<{ key: string; label: string; extractor: (r: any) => string }> = [
    { key: 'identification', label: 'Identificação', extractor: (r) => r.identification || '' },
    { key: 'descricao', label: 'Descrição', extractor: (r) => r.description || '' },
    { key: 'instituicao', label: 'Instituição', extractor: (r) => opts.instMap.get(r.institutionId)?.name || '' },
    { key: 'valor', label: 'Valor da Emenda', extractor: (r) => String(r.value || 0) },
    { key: 'utilizado', label: 'Utilizado (Vereador)', extractor: (r) => String(r.councilorUsed || 0) },
    { key: 'limite', label: 'Limite (Vereador)', extractor: (r) => String(r.councilorLimit || 0) },
    { key: 'categoria', label: 'Categoria', extractor: (r) => r.category || '' },
    { key: 'vereador', label: 'Nome', extractor: (r) => opts.counMap.get(r.councilorId)?.name || '' },
    { key: 'data', label: 'Data', extractor: (r) => r.date || '' },
  ];

  const colByKey = new Map(AVAILABLE_CSV_COLUMNS.map((c) => [c.key, c]));
  const sanitize = (s: string) => String(s).replaceAll('\r', ' ').replaceAll('\n', ' ').replaceAll(';', ',').trim();

  const headerLabels = selectedKeys.map((k) => colByKey.get(k)?.label || k);
  const header = headerLabels.join(';');
  const lines = rows.map((r) => selectedKeys.map((k) => sanitize(colByKey.get(k)?.extractor(r) ?? '')).join(';'));
  return [header, ...lines].join('\n');
}

// Componente para formatação de valor monetário
const MoneyInput = ({ id, name, placeholder, value, onChange, required = false }: {
  id: string;
  name: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
}) => {
  const formatCurrency = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    const cents = parseInt(cleanValue || '0', 10);
    const amount = cents / 100;

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatCurrency(e.target.value);
    e.target.value = formattedValue;
    onChange?.(e.target.value);
  };

  return (
    <Input
      id={id}
      name={name}
      placeholder={placeholder}
      defaultValue={value}
      onChange={handleChange}
      required={required}
    />
  );
};

// ===== Componente: Tabela Administrativa de Emendas =====
function AdminAmendmentsTable({ amendments, councilors, institutions, isAdminUser }: {
  amendments: Amendment[];
  councilors: Councilor[];
  institutions: Institution[];
  isAdminUser: boolean;
}) {
  // Seleção de colunas para exportação CSV
  const DEFAULT_SELECTION_KEYS = ['identification', 'descricao', 'valor', 'utilizado', 'limite', 'categoria'];
  const [selectedCsvKeys, setSelectedCsvKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('adminAmendmentsCsvColumns');
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_SELECTION_KEYS;
    } catch { return DEFAULT_SELECTION_KEYS; }
  });

  const [globalQuery, setGlobalQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | 'ALL'>('ALL');
  const [sortKey, setSortKey] = useState<'identification' | 'description' | 'value' | 'category'>('identification');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [prevSeqMap, setPrevSeqMap] = useState<Map<string, string>>(new Map());
  const [seqChangeLog, setSeqChangeLog] = useState<Array<{ id: string; from: string; to: string; identBefore: string; identAfter: string }>>([]);

  const formatBRL = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

  // Mapas auxiliares
  const instMap = new Map(institutions.map(i => [i.id, i]));
  const counMap = new Map(councilors.map(c => [c.id, c]));

  // Definição das colunas disponíveis para seleção
  const AVAILABLE_CSV_COLUMNS: Array<{ key: string; label: string }> = [
    { key: 'identification', label: 'Identificação' },
    { key: 'descricao', label: 'Descrição' },
    { key: 'instituicao', label: 'Instituição' },
    { key: 'valor', label: 'Valor da Emenda' },
    { key: 'utilizado', label: 'Utilizado (Vereador)' },
    { key: 'limite', label: 'Limite (Vereador)' },
    { key: 'categoria', label: 'Categoria' },
    { key: 'vereador', label: 'Nome' },
    { key: 'data', label: 'Data' },
  ];

  // Persistência em localStorage
  useEffect(() => {
    try { localStorage.setItem('adminAmendmentsCsvColumns', JSON.stringify(selectedCsvKeys)); } catch { }
  }, [selectedCsvKeys]);

  // Código do vereador (3 dígitos) por ordem alfabética
  const councilorNumberById = councilorCodeMap(councilors);
  const sortedCouncilors = [...councilors].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  // Número sequencial global das emendas, ordenando por código do vereador, depois data e id
  const amendmentNumberById = recomputeGlobalSequentialNumbersByCouncilor(amendments, councilorNumberById);

  // Log de renumeração: detecta mudanças de sequencial e registra
  useEffect(() => {
    const changes: Array<{ id: string; from: string; to: string; identBefore: string; identAfter: string }> = [];
    for (const a of amendments) {
      const prev = prevSeqMap.get(a.id);
      const curr = amendmentNumberById.get(a.id);
      if (prev && curr && prev !== curr) {
        const cNum = councilorNumberById.get(a.councilorId) || '000';
        const identBefore = buildIdentification(cNum, prev, a.date);
        const identAfter = buildIdentification(cNum, curr, a.date);
        changes.push({ id: a.id, from: prev, to: curr, identBefore, identAfter });
      }
    }
    if (changes.length > 0) setSeqChangeLog(changes);
    setPrevSeqMap(new Map(amendmentNumberById));
  }, [amendments, amendmentNumberById, councilorNumberById, prevSeqMap]);

  // Agregados por vereador (utilizado vs limite)
  const usedByCouncilor = new Map<string, number>();
  amendments.forEach(a => {
    const prev = usedByCouncilor.get(a.councilorId) || 0;
    usedByCouncilor.set(a.councilorId, prev + (a.value || 0));
  });

  // Linhas enriquecidas com campos calculados
  const enriched = amendments.map(a => {
    const councilor = counMap.get(a.councilorId);
    const institution = instMap.get(a.institutionId);
    const cNum = councilorNumberById.get(a.councilorId) || '999';
    const aNum = amendmentNumberById.get(a.id) || '000';
    const year = (a.date || '').slice(0, 4) || '';
    const identification = buildIdentification(cNum, aNum!, a.date);
    const descriptionText = `${a.description || ''}${institution ? ` - ${institution.name}` : ''}`.trim();
    const category = institution?.category || 'SEM CATEGORIA';
    const councilorUsed = usedByCouncilor.get(a.councilorId) || 0;
    const councilorLimit = councilor?.totalLimit || 0;
    const remaining = councilorLimit - councilorUsed;
    const criticalLevel: 'ok' | 'near' | 'over' = remaining < 0 ? 'over' : (councilorLimit > 0 && remaining / councilorLimit <= 0.1 ? 'near' : 'ok');
    return {
      ...a,
      identification,
      cNum,
      aNum,
      descriptionText,
      category,
      councilorUsed,
      councilorLimit,
      remaining,
      criticalLevel,
    };
  });

  // Filtros
  const byCategory = categoryFilter === 'ALL' ? enriched : enriched.filter(r => r.category === categoryFilter);
  const byGlobal = globalQuery.trim() ? byCategory.filter(r => {
    const q = globalQuery.toLowerCase();
    return (
      r.identification.toLowerCase().includes(q) ||
      r.descriptionText.toLowerCase().includes(q) ||
      r.category.toLowerCase().includes(q) ||
      String(r.value).includes(q)
    );
  }) : byCategory;

  // Ordenação
  const sorted = [...byGlobal].sort((a, b) => {
    const ac = parseInt(a.cNum || '0', 10);
    const bc = parseInt(b.cNum || '0', 10);
    const aa = parseInt(a.aNum || '0', 10);
    const ba = parseInt(b.aNum || '0', 10);
    let cmp = ac - bc || aa - ba;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Validação de continuidade da sequência 001..N no conjunto atual
  const seqIsContinuous = (() => {
    const nums = Array.from(amendmentNumberById.values()).map(n => parseInt(n || '0', 10)).filter(n => n > 0).sort((x, y) => x - y);
    for (let i = 0; i < nums.length; i++) {
      if (nums[i] !== i + 1) return false;
    }
    return true;
  })();

  // Validação de sequência numérica dos vereadores e lacunas (dentro do componente)
  const expectedCouncilorNums = sortedCouncilors.map((_, idx) => String(idx + 1).padStart(3, '0'));
  const presentCouncilorNums = Array.from(new Set(sorted.map(r => (r.identification.split('-')[0])))).sort();
  const missingCouncilorNums = expectedCouncilorNums.filter(n => !presentCouncilorNums.includes(n));
  const councilorsWithoutAmendments = sortedCouncilors.filter(c => !amendments.some(a => a.councilorId === c.id));

  // Paginação
  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = sorted.slice(pageStart, pageStart + pageSize);

  // Export CSV
  const exportCSV = () => {
    const csv = generateAmendmentsCsv(sorted.map(r => ({
      ...r,
      description: r.description, // garantir campo cru de descrição
      descriptionText: r.descriptionText,
    })), selectedCsvKeys, { instMap, counMap });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'emendas.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Refresh manual
  const refreshNow = async () => {
    try {
      const list = await listAmendments();
      // Não alteramos estados externos aqui; este refresh é local, apenas força re-render ao alterar paginação/sort.
      // Como os dados vêm por props, esta função serve para usuários enquanto o snapshot sincroniza.
      // Podemos apenas reiniciar paginação.
      setPage(1);
    } catch { }
  };

  // Controle de grupos
  const toggleGroup = (cat: string) => setCollapsedGroups((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const categories = Array.from(new Set(institutions.map(i => i.category))).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDesc, setEditDesc] = useState('');
  const [editSignedLink, setEditSignedLink] = useState('');
  const [editSeq, setEditSeq] = useState('');
  const [editDetailingLocked, setEditDetailingLocked] = useState(false);
  const [editFederalStatus, setEditFederalStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const currentEditing = editingId ? amendments.find(a => a.id === editingId) || null : null;
  const openEdit = (id: string) => {
    const a = amendments.find(x => x.id === id);
    if (!a) return;
    setEditingId(id);
    setEditDesc(a.description || '');
    setEditSignedLink(a.signedLink || '');
    const seqFromOfficial = (() => {
      const raw = a.officialCode || '';
      const parts = raw.split('-');
      if (parts.length >= 3) return parts[1] || '';
      return '';
    })();
    const fallbackSeq = amendmentNumberById.get(id) || '';
    setEditSeq(seqFromOfficial || fallbackSeq || '');
    setEditDetailingLocked(!!a.detailingSaved);
    setEditFederalStatus(a.federalStatus || '');
    setFormError(null);
  };
  const closeEdit = () => {
    setEditingId(null);
    setSaving(false);
    setFormError(null);
  };
  const validate = () => {
    const d = (editDesc || '').trim();
    if (d.length < 3 || d.length > 1000) return 'Descrição deve ter entre 3 e 1000 caracteres';
    const seq = (editSeq || '').trim();
    if (!/^[0-9]{1,3}$/.test(seq)) return 'Número da emenda deve ser um inteiro entre 1 e 999';
    const padded = String(parseInt(seq, 10)).padStart(3, '0');
    const a = currentEditing!;
    const cNum = councilorNumberById.get(a.councilorId) || '999';
    const year = (a.date || '').slice(0, 4) || '';
    const nextOfficial = `${cNum}-${padded}-${year}`;
    const dup = amendments.some(x => x.id !== editingId && (x.officialCode || '') === nextOfficial);
    if (dup) return 'Identificador já utilizado em outra emenda';
    const link = (editSignedLink || '').trim();
    if (link) {
      try { new URL(link); } catch { return 'Emenda Assinada deve ser uma URL válida'; }
    }
    return null;
  };
  const saveEdit = async () => {
    if (!editingId || !currentEditing) return;
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true);
    try {
      const nextDesc = (editDesc || '').trim();
      const seq = String(parseInt((editSeq || '0'), 10));
      const padded = String(Math.max(1, Math.min(999, parseInt(seq || '0', 10)))).padStart(3, '0');
      const a = currentEditing!;
      const cNum = councilorNumberById.get(a.councilorId) || '999';
      const year = (a.date || '').slice(0, 4) || '';
      const nextOfficial = `${cNum}-${padded}-${year}`;
      const link = (editSignedLink || '').trim();
      const nextLocked = !!editDetailingLocked;

      await updateAmendment(editingId, {
        description: nextDesc,
        officialCode: nextOfficial,
        signedLink: link || undefined,
        detailingSaved: nextLocked,
        federalStatus: (editFederalStatus || '').trim()
      });

      const actorUid = auth.currentUser?.uid || '';
      const actorEmail = auth.currentUser?.email || localStorage.getItem('userEmail') || '';
      await logAdminAction({ actorUid, actorEmail, action: 'update', entity: 'amendment', entityId: editingId, entityName: currentEditing.description, dateTime: new Date().toISOString() });
      const changes: Array<{ field: string; from?: any; to?: any }> = [];
      if ((currentEditing.description || '') !== nextDesc) changes.push({ field: 'description', from: currentEditing.description, to: nextDesc });
      if ((currentEditing.officialCode || undefined) !== nextOfficial) changes.push({ field: 'officialCode', from: currentEditing.officialCode, to: nextOfficial });
      if ((currentEditing.signedLink || '') !== link) changes.push({ field: 'signedLink', from: currentEditing.signedLink, to: link });
      if (!!currentEditing.detailingSaved !== nextLocked) changes.push({ field: 'detailingSaved', from: !!currentEditing.detailingSaved, to: nextLocked });
      if ((currentEditing.federalStatus || '') !== (editFederalStatus || '').trim()) changes.push({ field: 'federalStatus', from: currentEditing.federalStatus, to: (editFederalStatus || '').trim() });

      if (changes.length > 0) {
        await logAmendmentHistory(editingId, { actorUid, actorEmail, dateTime: new Date().toISOString(), changes });
      }
      toast({ title: 'Emenda atualizada', description: 'As alterações foram salvas.' });
      closeEdit();
    } catch {
      setSaving(false);
      toast({ title: 'Falha ao salvar', description: 'Não foi possível atualizar a emenda.', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtros e ações */}
      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <Label htmlFor="globalSearch">Busca global</Label>
          <Input id="globalSearch" placeholder="Filtrar em todas as colunas" value={globalQuery} onChange={(e) => setGlobalQuery(e.target.value)} />
        </div>
        <div className="w-full md:w-64">
          <Label htmlFor="categoryFilter">Categoria</Label>
          <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as any)}>
            <SelectTrigger id="categoryFilter"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              {categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {/* Seleção de colunas para exportação CSV */}
        <div className="flex flex-col gap-2 w-full md:w-auto">
          <div className="flex items-center gap-2 flex-wrap">
            {AVAILABLE_CSV_COLUMNS.map((col) => {
              const checked = selectedCsvKeys.includes(col.key);
              return (
                <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(val) => {
                      const isOn = !!val;
                      setSelectedCsvKeys((prev) => {
                        if (isOn && !prev.includes(col.key)) return [...prev, col.key];
                        if (!isOn) return prev.filter((k) => k !== col.key);
                        return prev;
                      });
                    }}
                  />
                  {col.label}
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedCsvKeys(AVAILABLE_CSV_COLUMNS.map(c => c.key))}>Selecionar todas</Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedCsvKeys(DEFAULT_SELECTION_KEYS)}>Padrão do sistema</Button>
            <Button variant="outline" onClick={exportCSV}><i className="fas fa-file-export mr-2"></i>Exportar CSV</Button>
            <Button variant="secondary" onClick={refreshNow}><i className="fas fa-sync mr-2"></i>Atualizar</Button>
          </div>
        </div>
      </div>

      {/* Cabeçalho com ordenação */}
      <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-2 bg-muted rounded">
        <button className="text-left col-span-2 font-medium" onClick={() => { setSortKey('identification'); setSortDir(sortKey === 'identification' && sortDir === 'asc' ? 'desc' : 'asc'); }}>Identificação</button>
        <button className="text-left col-span-6 font-medium" onClick={() => { setSortKey('description'); setSortDir(sortKey === 'description' && sortDir === 'asc' ? 'desc' : 'asc'); }}>Descrição</button>
        <button className="text-left col-span-2 font-medium" onClick={() => { setSortKey('value'); setSortDir(sortKey === 'value' && sortDir === 'asc' ? 'desc' : 'asc'); }}>Valores</button>
        <button className="text-left col-span-2 font-medium" onClick={() => { setSortKey('category'); setSortDir(sortKey === 'category' && sortDir === 'asc' ? 'desc' : 'asc'); }}>Categoria</button>
      </div>

      {/* Validação de sequência e estados especiais */}
      <div className="space-y-2">
        {!seqIsContinuous && (
          <div className="px-3 py-2 rounded border border-red-300 bg-red-50 text-red-900 text-sm">
            Inconsistência detectada na sequência numérica. A renumeração foi aplicada para corrigir.
          </div>
        )}
        {missingCouncilorNums.length > 0 && (
          <div className="px-3 py-2 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
            Lacunas na sequência: {missingCouncilorNums.join(', ')}
          </div>
        )}
        {councilorsWithoutAmendments.length > 0 && (
          <div className="px-3 py-2 rounded border border-muted bg-muted text-foreground/80 text-sm">
            Vereadores sem emendas: {councilorsWithoutAmendments.map(c => councilorNumberById.get(c.id)).filter(Boolean).join(', ')}
          </div>
        )}
        {seqChangeLog.length > 0 && (
          <div className="px-3 py-2 rounded border border-blue-300 bg-blue-50 text-blue-900 text-sm">
            <div className="font-medium mb-1">Renumeração aplicada ({seqChangeLog.length} alterações):</div>
            <ul className="list-disc ml-5 space-y-1">
              {seqChangeLog.map((chg) => (
                <li key={chg.id}><span className="font-mono">{chg.identBefore}</span> → <span className="font-mono">{chg.identAfter}</span></li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Corpo: agrupado por vereador, e dentro por categoria */}
      <div className="space-y-6">
        {(() => {
          // Agrupa por vereador
          const byCoun = new Map<string, typeof sorted>();
          for (const r of sorted) {
            const arr = byCoun.get(r.cNum) || [];
            arr.push(r);
            byCoun.set(r.cNum, arr);
          }
          // Ordena vereadores pelo código
          const counEntries = Array.from(byCoun.entries()).sort((a, b) => a[0].localeCompare(b[0]));
          return counEntries.map(([cnum, rows]) => {
            // Agrupa por categoria dentro do vereador
            const byCat = new Map<string, typeof rows>();
            for (const r of rows) {
              const arr = byCat.get(r.category) || [];
              arr.push(r);
              byCat.set(r.category, arr);
            }
            const catEntries = Array.from(byCat.entries()).sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
            return (
              <section key={`c-${cnum}`} className="space-y-3">
                <div className="text-sm font-semibold text-muted-foreground">Vereador {cnum}</div>
                {catEntries.map(([cat, items]) => (
                  <div key={`${cnum}-${cat}`} className="space-y-2">
                    {[...items]
                      .sort((a, b) => {
                        const aa = parseInt(a.aNum || '0', 10);
                        const bb = parseInt(b.aNum || '0', 10);
                        return aa - bb;
                      })
                      .map((r) => (
                        <div key={r.id} className="grid grid-cols-12 gap-3 p-3 border rounded w-full overflow-hidden">
                          <div className="col-span-12 md:col-span-3 min-w-0">
                            <div className="font-mono truncate" title={r.identification}>{r.identification}</div>
                            {(r.officialCode && r.officialCode !== r.identification) ? (
                              <span className="mt-1 inline-block font-mono text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground truncate" title={r.officialCode}>{r.officialCode}</span>
                            ) : null}
                          </div>
                          <div className="col-span-12 md:col-span-5 min-w-0">
                            <div className="font-medium whitespace-normal break-words" title={r.descriptionText || '-'}>{r.descriptionText || '-'}</div>
                          </div>
                          <div className="col-span-6 md:col-span-2">
                            <div className="font-bold text-right md:text-left">{formatBRL(r.value || 0)}</div>
                          </div>
                          <div className="col-span-6 md:col-span-2 flex flex-wrap items-center gap-2 justify-between md:justify-end">
                            <span className="text-sm font-medium shrink-0">{r.category}</span>
                            {r.criticalLevel === 'over' ? (
                              <span className="px-2 py-1 text-xs rounded bg-destructive text-destructive-foreground shrink-0">Ultrapassou limite</span>
                            ) : r.criticalLevel === 'near' ? (
                              <span className="px-2 py-1 text-xs rounded bg-amber-200 text-amber-900 shrink-0">Aproximando do limite</span>
                            ) : null}
                            {isAdminUser && (
                              <Button className="shrink-0" variant="outline" size="sm" onClick={() => openEdit(r.id)} aria-label="Editar emenda">
                                <i className="fas fa-edit mr-2"></i>
                                Editar
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
              </section>
            );
          });
        })()}
      </div>


      {/* Paginação */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">Página {currentPage} de {totalPages} • {totalItems} registros</div>
        <div className="flex items-center gap-2">
          <Label htmlFor="pageSize">Itens por página</Label>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v, 10)); setPage(1); }}>
            <SelectTrigger id="pageSize"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" disabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><i className="fas fa-chevron-left"></i></Button>
          <Button variant="outline" disabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}><i className="fas fa-chevron-right"></i></Button>
        </div>
      </div>
      <Dialog open={!!editingId} onOpenChange={(open) => (open ? null : closeEdit())}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-[720px]">
          <DialogHeader>
            <DialogTitle id="edit-amendment-title">Edição de Emenda</DialogTitle>
            <DialogDescription id="edit-amendment-desc">Atualize descrição e código da emenda.</DialogDescription>
          </DialogHeader>
          {currentEditing && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-seq">Número da Emenda</Label>
                <Input id="edit-seq" type="number" min={1} max={999} value={editSeq} onChange={(e) => setEditSeq(e.target.value)} placeholder="Somente o número sequencial (001..999)" />
              </div>
              <div>
                <Label htmlFor="edit-signed-link">Emenda Assinada</Label>
                <Input id="edit-signed-link" type="url" value={editSignedLink} onChange={(e) => setEditSignedLink(e.target.value)} placeholder="https://exemplo.com/emenda-assinada.pdf" />
              </div>
              <div>
                <Label htmlFor="edit-desc">Descrição</Label>
                <Textarea id="edit-desc" rows={6} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-detailing-locked"
                  checked={editDetailingLocked}
                  onCheckedChange={(c) => setEditDetailingLocked(!!c)}
                />
                <Label htmlFor="edit-detailing-locked" className="cursor-pointer">
                  Detalhamento Enviado/Bloqueado (desmarque para permitir edição pela instituição)
                </Label>
              </div>
              <div>
                <Label htmlFor="edit-federal-status">Situação Federal</Label>
                <Input id="edit-federal-status" value={editFederalStatus} onChange={(e) => setEditFederalStatus(e.target.value)} placeholder="Ex: Pago, Empenhada, Liquidada, etc." />
                <div className="mt-2 flex flex-wrap gap-2">
                  {["Em análise", "Aguardando empenho", "Empenhada", "Liquidada", "Pago", "Aguardando liberação", "Cancelada"].map(opt => (
                    <Button key={opt} type="button" variant="outline" size="sm" onClick={() => setEditFederalStatus(opt)}>
                      {opt}
                    </Button>
                  ))}
                </div>
              </div>
              {formError && (<p className="text-sm text-destructive">{formError}</p>)}
              <DialogFooter>
                <Button variant="outline" onClick={closeEdit}>Cancelar</Button>
                <Button onClick={saveEdit} disabled={saving}>
                  <i className="fas fa-save mr-2"></i>
                  {saving ? 'Salvando…' : 'Salvar'}
                </Button >
              </DialogFooter >
            </div >
          )
          }
        </DialogContent >
      </Dialog >
    </div >
  );
}

export default function SystemAdministratorArea() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Proteção de rota é tratada pelo ProtectedRoute

  const [allocationsLocked, setAllocationsLocked] = useState(false);

  const [lockSchedule, setLockSchedule] = useState({
    enabled: false,
    date: '',
    time: ''
  });

  const [instFormSchedule, setInstFormSchedule] = useState<{ enabled: boolean; startDate: string; startTime: string; endDate: string; endTime: string; notify?: boolean; completed?: boolean }>({ enabled: false, startDate: '', startTime: '', endDate: '', endTime: '', notify: false, completed: false });

  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [editingInstitutionId, setEditingInstitutionId] = useState<string | null>(null);
  const [editInstitutionValues, setEditInstitutionValues] = useState<{ name: string; category: Institution['category']; link: string; cnpj: string; legalName?: string; contactEmail?: string; contactPhone?: string; responsibleEmail?: string }>({ name: '', category: 'SAÚDE', link: '', cnpj: '', legalName: '', contactEmail: '', contactPhone: '', responsibleEmail: '' });
  const [instPwd, setInstPwd] = useState<string>('');
  const [instPwdConfirm, setInstPwdConfirm] = useState<string>('');
  const [instPwdShow, setInstPwdShow] = useState<boolean>(false);
  const [editInstPwd, setEditInstPwd] = useState<string>('');
  const [editInstPwdConfirm, setEditInstPwdConfirm] = useState<string>('');
  const [editInstPwdShow, setEditInstPwdShow] = useState<boolean>(false);
  const [institutionQuery, setInstitutionQuery] = useState<string>('');
  const isStrongPassword = (pwd: string) => pwd.length >= 8 && /[A-Za-z]/.test(pwd) && /\d/.test(pwd);

  const filteredInstitutions = useMemo(() => {
    const q = institutionQuery.trim().toLowerCase();
    if (!q) return institutions;
    return institutions.filter((i) => {
      const nameMatch = (i.name || '').toLowerCase().includes(q);
      const cnpjDigits = (i.cnpj || '').replace(/\D/g, '');
      const cnpjMatch = cnpjDigits.includes(q.replace(/\D/g, ''));
      const codeMatch = (i.id || '').toLowerCase().includes(q);
      return nameMatch || cnpjMatch || codeMatch;
    });
  }, [institutions, institutionQuery]);

  const groupsByCnpj = useMemo(() => {
    const map = new Map<string, Institution[]>();
    for (const i of filteredInstitutions) {
      const key = (i.cnpj || '').replace(/\D/g, '') || 'SEM-CNPJ';
      const arr = map.get(key) || [];
      arr.push(i);
      map.set(key, arr);
    }
    const groups = Array.from(map.entries()).map(([key, items]) => ({ key, label: key === 'SEM-CNPJ' ? 'Sem CNPJ' : formatCnpj(key), items: items.sort((a, b) => (a.name || '').localeCompare(b.name || '')) }));
    groups.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
  }, [filteredInstitutions]);
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState<Set<string>>(new Set());

  const [councilors, setCouncilors] = useState<Councilor[]>([]);
  const [editingCouncilorId, setEditingCouncilorId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ name: string; party: string; totalLimit: string; email: string; photoUrl: string }>({ name: '', party: '', totalLimit: '', email: '', photoUrl: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [councilorToDelete, setCouncilorToDelete] = useState<Councilor | null>(null);
  // CSV import state
  const [csvFile, setCsvFile] = useState<File | null>(null);

  // Modelos de descrição
  const [templates, setTemplates] = useState<DescriptionTemplate[]>([]);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editTemplateValues, setEditTemplateValues] = useState<{ text: string; active: boolean }>({ text: '', active: true });

  const [consolidatedOpen, setConsolidatedOpen] = useState(false);
  const [consolidatedView, setConsolidatedView] = useState<{ ids: string[]; items: Amendment[]; total: number; cnpj: string } | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Emendas (para tabela administrativa)
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [amendmentsLoaded, setAmendmentsLoaded] = useState<boolean>(false);

  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [detailViewItem, setDetailViewItem] = useState<any | null>(null);

  const exportCsvRows = (rows: any[]) => {
    const instMap = new Map(institutions.map(i => [i.id, i]));
    const counMap = new Map(councilors.map(c => [c.id, c]));
    const esc = (s: string) => '"' + String(s).replaceAll('"', '""') + '"';
    const fmtBRL = (n: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);
    const buildNum = (r: any) => {
      const cNum = councilorCodeMap(councilors).get(r.councilorId) || '999';
      const parts = (r.officialCode || '').split('-');
      const seqRaw = parts.length >= 3 ? parts[1] : String(parseInt(String(r.aNum || 0), 10) || 0).padStart(3, '0');
      const ident = buildIdentification(cNum, seqRaw, r.date);
      return r.officialCode || ident;
    };
    const head = [
      'Número da Emenda',
      'Parlamentar responsável',
      'Categoria da emenda',
      'Beneficiária (entidade)',
      'CNPJ da beneficiária',
      'Detalhamento do objeto',
      'Classificação da emenda',
      'Nome do projeto ou ação',
      'Valor total',
      'Finalidade de uso',
      'Prazo de execução',
      'Emenda assinada (link)',
      'Anexos (links)'
    ].map(esc).join(',');
    const warn: string[] = [];
    const lines = rows.map(r => {
      const inst = instMap.get(r.institutionId);
      const cnpj = inst?.cnpj || '';
      const obj = r.objectDetail || '';
      const proj = r.workPlan?.projectName || '';
      const val = fmtBRL(r.value || 0);
      const purp = r.workPlan?.purpose || '';
      const period = r.workPlan?.period || '';
      const signed = r.signedLink || '';
      const allAttachments = Array.isArray(r.attachments) ? r.attachments : [];
      const attachmentsJoined = allAttachments.length > 0 ? allAttachments.join(' | ') : '(sem anexo)';
      const cls = r.classification || '';
      const missing: string[] = [];
      if (!cnpj) missing.push('CNPJ');
      if (!obj) missing.push('Detalhamento');
      if (!cls) missing.push('Classificação');
      if (!proj) missing.push('Projeto/Ação');
      if (!purp) missing.push('Finalidade');
      if (!period) missing.push('Prazo');
      if (missing.length) warn.push(`${buildNum(r)}: ${missing.join(', ')}`);
      const row = [
        buildNum(r),
        counMap.get(r.councilorId)?.name || '',
        r.category || inst?.category || '',
        inst?.name || '',
        cnpj,
        obj,
        cls || '-',
        proj,
        val,
        purp,
        period,
        signed || '(sem emenda assinada)',
        attachmentsJoined
      ].map(esc).join(',');
      return row;
    });
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const name = `emendas_${yyyy}-${mm}-${dd}.csv`;
    const content = ['\uFEFF' + head, ...lines].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    if (warn.length) toast({ title: 'Dados incompletos', description: `${warn.length} registro(s) com campos ausentes: ${warn.slice(0, 5).join(' | ')}${warn.length > 5 ? '…' : ''}` });
  };

  // Admins
  type AdminUser = { id: string; email?: string; adminLevel?: 'principal' | 'padrao' };
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [adminInstitutionByUid, setAdminInstitutionByUid] = useState<Record<string, string>>({});
  const [registerAdminInstitutionId, setRegisterAdminInstitutionId] = useState<string>('');
  const [isSubmittingAdmin, setIsSubmittingAdmin] = useState(false);
  const [lastAdminCreation, setLastAdminCreation] = useState<{ email: string; verificationLink?: string; uid?: string } | null>(null);
  const [adminMasterPassword, setAdminMasterPassword] = useState<string>('');
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false);

  // Carrega dados iniciais
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    (async () => {
      try {
        const [insts, couns, settings] = await Promise.all([
          listInstitutions(),
          listCouncilors(),
          getSettings(),
        ]);
        setInstitutions(insts);
        setCouncilors(couns);
        if (settings) {
          setAllocationsLocked(settings.allocationsLocked ?? false);
          setLockSchedule(settings.lockSchedule ?? { enabled: false, date: '', time: '' });
          setInstFormSchedule(settings.institutionFormSchedule ?? { enabled: false, startDate: '', startTime: '', endDate: '', endTime: '', notify: false, completed: false });
        }
        // Carrega modelos de descrição
        try {
          const descTemplates = await listDescriptionTemplates();
          setTemplates(descTemplates);
        } catch {
          // silencioso: se falhar, apenas não popula
        }
        // Carrega emendas inicialmente e assina atualizações em tempo real
        try {
          const initialAmendments = await listAmendments();
          setAmendments(initialAmendments);
          setAmendmentsLoaded(true);
        } catch {
          setAmendmentsLoaded(false);
        }
        unsubscribe = onSnapshot(amendmentsCol, (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) }));
          setAmendments(list);
          if (!amendmentsLoaded) setAmendmentsLoaded(true);
        });
        // Carrega administradores via servidor administrativo (Admin SDK) com fallback
        try {
          const resp = await adminGet('/admins');
          if (resp.ok) {
            const data = await resp.json();
            const list: AdminUser[] = (data.admins || []).map((d: any) => ({ id: d.id, email: d.email, adminLevel: d.adminLevel }));
            setAdmins(list);
          } else {
            const { listAdminsClient } = await import('@/services/firestore');
            const list = await listAdminsClient();
            setAdmins(list);
          }
        } catch {
          try {
            const { listAdminsClient } = await import('@/services/firestore');
            const list = await listAdminsClient();
            setAdmins(list);
          } catch (e) {
            console.warn('Falha ao listar administradores (server e fallback)', e);
          }
        }
      } catch {
        toast({ title: 'Erro ao carregar dados', description: 'Falha ao acessar Firestore.', variant: 'destructive' });
      }
    })();
    return () => { try { unsubscribe && unsubscribe(); } catch { } };
  }, [toast]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setIsAdminUser(false); return; }
    (async () => {
      let ok = false;
      try {
        const token = await getIdTokenResult(auth.currentUser!);
        ok = (token.claims?.role as any) === 'admin';
      } catch { }
      if (!ok) {
        try {
          const role = await getUserRole(uid);
          ok = role === 'admin';
        } catch { }
      }
      if (!ok) {
        ok = admins.some((a) => a.id === uid);
      }
      setIsAdminUser(ok);
    })();
  }, [admins]);

  const handleConfirmDeleteCouncilor = async () => {
    if (!councilorToDelete) return;
    try {
      await deleteCouncilor(councilorToDelete.id);
      const actorUid = auth.currentUser?.uid || '';
      const actorEmail = auth.currentUser?.email || localStorage.getItem('userEmail') || '';
      await logAdminAction({
        actorUid,
        actorEmail,
        action: 'delete',
        entity: 'councilor',
        entityId: councilorToDelete.id,
        entityName: councilorToDelete.name,
        dateTime: new Date().toISOString(),
      });
      const updated = await listCouncilors();
      setCouncilors(updated);
      toast({ title: 'Vereador excluído', description: `${councilorToDelete.name} removido com sucesso.` });
    } catch (e) {
      toast({ title: 'Erro ao excluir', description: 'Não foi possível excluir o vereador.', variant: 'destructive' });
    } finally {
      setDeleteDialogOpen(false);
      setCouncilorToDelete(null);
    }
  };

  const handleAddTemplate = async (formData: FormData) => {
    const text = (formData.get('templateText') as string)?.trim();
    const activeStr = formData.get('templateActive') as string;
    const active = activeStr === 'true' || activeStr === 'active';

    if (!text || text.length < 3 || text.length > 600) {
      toast({ title: 'Descrição inválida', description: 'O modelo deve ter entre 3 e 600 caracteres.', variant: 'destructive' });
      return;
    }

    try {
      await addDescriptionTemplate({ text, active });
      const updated = await listDescriptionTemplates();
      setTemplates(updated);
      toast({ title: 'Modelo cadastrado', description: 'Modelo de descrição adicionado com sucesso.' });
    } catch {
      toast({ title: 'Erro ao cadastrar modelo', description: 'Tente novamente mais tarde.', variant: 'destructive' });
    }
  };

  // ===== Admin management =====
  const handleRegisterAdmin = async (formData: FormData) => {
    const name = (formData.get('adminName') as string) || '';
    const email = (formData.get('adminEmail') as string) || '';
    const password = (formData.get('adminPassword') as string) || '';
    const level = (formData.get('permissionLevel') as string) || 'padrao';
    const masterPassword = (formData.get('masterPassword') as string) || '';
    const adminInstitutionId = (formData.get('adminInstitutionId') as string) || '';

    if (!name || !email || !password || !level || !masterPassword) {
      toast({ title: 'Dados incompletos', description: 'Preencha todos os campos obrigatórios.', variant: 'destructive' });
      return;
    }
    setIsSubmittingAdmin(true);
    try {
      const actorEmail = auth.currentUser?.email || localStorage.getItem('userEmail') || '';
      const actorUid = auth.currentUser?.uid || '';
      const resp = await adminPost('/register-admin', { email, password, displayName: name, permissionLevel: level, actorEmail, actorUid, masterPassword });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const code = err.error || `HTTP ${resp.status}`;
        const msg = code === 'invalid-master-password'
          ? 'Senha mestra inválida.'
          : code === 'admin-limit-reached'
            ? 'Limite de administradores atingido.'
            : code === 'ip-not-allowed'
              ? 'Seu IP não está autorizado para esta ação.'
              : 'Falha ao criar administrador.';
        throw new Error(msg);
      }
      const data = await resp.json();
      toast({ title: 'Administrador criado', description: `Conta para ${email} foi criada.` });
      setLastAdminCreation({ email, verificationLink: data.verificationLink, uid: data.uid });
      if (data.verificationLink) {
        toast({ title: 'Verificação de email', description: 'Link de verificação gerado. Veja detalhes abaixo e copie se necessário.' });
      }
      if (adminInstitutionId) {
        try { await setUserInstitution(data.uid, adminInstitutionId); } catch { }
      }
      // Atualiza lista de admins via servidor
      try {
        const respList = await adminGet('/admins');
        if (respList.ok) {
          const d = await respList.json();
          const list: AdminUser[] = (d.admins || []).map((x: any) => ({ id: x.id, email: x.email, adminLevel: x.adminLevel }));
          setAdmins(list);
        }
      } catch { }
    } catch (e) {
      toast({ title: 'Erro ao criar administrador', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setIsSubmittingAdmin(false);
    }
  };

  useEffect(() => {
    (async () => {
      const entries: [string, string][] = [];
      for (const a of admins) {
        try {
          const instId = await getUserInstitutionId(a.id);
          entries.push([a.id, instId || '']);
        } catch {
          entries.push([a.id, '']);
        }
      }
      setAdminInstitutionByUid(Object.fromEntries(entries));
    })();
  }, [admins]);

  const handleSetAdminLevel = async (uid: string, adminLevel: 'principal' | 'padrao') => {
    try {
      const masterPassword = adminMasterPassword.trim();
      if (!masterPassword) {
        toast({ title: 'Informe a senha mestra', description: 'Preencha o campo de senha mestra acima para alterar o nível.', variant: 'destructive' });
        return;
      }
      const actorEmail = auth.currentUser?.email || localStorage.getItem('userEmail') || '';
      const actorUid = auth.currentUser?.uid || '';
      const resp = await adminPost('/admin/set-level', { uid, adminLevel, actorEmail, actorUid, masterPassword });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const code = err.error || `HTTP ${resp.status}`;
        const msg = code === 'invalid-master-password'
          ? 'Senha mestra inválida.'
          : code === 'ip-not-allowed'
            ? 'Seu IP não está autorizado para esta ação.'
            : 'Falha ao atualizar nível.';
        throw new Error(msg);
      }
      toast({ title: 'Nível atualizado', description: 'Permissões do administrador atualizadas.' });
      // Refresh list
      try {
        const respList = await adminGet('/admins');
        if (respList.ok) {
          const d = await respList.json();
          const list: AdminUser[] = (d.admins || []).map((x: any) => ({ id: x.id, email: x.email, adminLevel: x.adminLevel }));
          setAdmins(list);
        }
      } catch { }
    } catch (e) {
      toast({ title: 'Erro', description: (e as Error).message, variant: 'destructive' });
    }
  };

  const handleRevokeAdmin = async (uid: string) => {
    try {
      const masterPassword = adminMasterPassword.trim();
      if (!masterPassword) {
        toast({ title: 'Informe a senha mestra', description: 'Preencha o campo de senha mestra acima para revogar acesso.', variant: 'destructive' });
        return;
      }
      const actorEmail = auth.currentUser?.email || localStorage.getItem('userEmail') || '';
      const actorUid = auth.currentUser?.uid || '';
      const resp = await adminPost('/admin/revoke', { uid, actorEmail, actorUid, masterPassword });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const code = err.error || `HTTP ${resp.status}`;
        const msg = code === 'invalid-master-password'
          ? 'Senha mestra inválida.'
          : code === 'ip-not-allowed'
            ? 'Seu IP não está autorizado para esta ação.'
            : 'Falha ao revogar acesso.';
        throw new Error(msg);
      }
      toast({ title: 'Acesso revogado', description: 'Administrador removido da lista de acesso.' });
      // Refresh list
      try {
        const respList = await fetch(`/admin-api/admins`);
        if (respList.ok) {
          const d = await respList.json();
          const list: AdminUser[] = (d.admins || []).map((x: any) => ({ id: x.id, email: x.email, adminLevel: x.adminLevel }));
          setAdmins(list);
        }
      } catch { }
    } catch (e) {
      toast({ title: 'Erro', description: (e as Error).message, variant: 'destructive' });
    }
  };

  const handleUpdateTemplate = async (id: string) => {
    const { text, active } = editTemplateValues;
    const trimmed = text.trim();
    if (!trimmed || trimmed.length < 3 || trimmed.length > 600) {
      toast({ title: 'Descrição inválida', description: 'O modelo deve ter entre 3 e 600 caracteres.', variant: 'destructive' });
      return;
    }
    try {
      await updateDescriptionTemplate(id, { text: trimmed, active });
      const updated = await listDescriptionTemplates();
      setTemplates(updated);
      setEditingTemplateId(null);
      toast({ title: 'Modelo atualizado', description: 'Alterações salvas com sucesso.' });
    } catch {
      toast({ title: 'Erro ao atualizar modelo', description: 'Não foi possível salvar as alterações.', variant: 'destructive' });
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await deleteDescriptionTemplate(id);
      const updated = await listDescriptionTemplates();
      setTemplates(updated);
      toast({ title: 'Modelo excluído', description: 'O modelo foi removido.' });
    } catch {
      toast({ title: 'Erro ao excluir modelo', description: 'Tente novamente mais tarde.', variant: 'destructive' });
    }
  };

  const handleAddInstitution = async (formData: FormData) => {
    const name = formData.get('institutionName') as string;
    const category = formData.get('category') as string;
    const link = formData.get('link') as string;
    const rawCnpj = (formData.get('cnpj') as string) || '';
    const cnpj = formatCnpj(rawCnpj);
    const legalName = (formData.get('legalName') as string) || '';
    const contactEmail = (formData.get('contactEmail') as string) || '';
    const contactPhone = (formData.get('contactPhone') as string) || '';
    const responsibleEmail = (formData.get('responsibleEmail') as string) || '';

    if (name && category && link && cnpj) {
      if (!validateCnpj(cnpj)) {
        toast({ title: 'CNPJ inválido', description: 'Verifique o número do CNPJ.', variant: 'destructive' });
        return;
      }
      if (!instPwd) {
        toast({ title: 'Defina a senha institucional', description: 'Uma senha por CNPJ é obrigatória.', variant: 'destructive' });
        return;
      }
      if (instPwd !== instPwdConfirm) {
        toast({ title: 'Senhas diferentes', description: 'Confirme a senha corretamente.', variant: 'destructive' });
        return;
      }
      if (!isStrongPassword(instPwd)) {
        toast({ title: 'Senha fraca', description: 'A senha deve ter ao menos 8 caracteres, letras e números.', variant: 'destructive' });
        return;
      }
      try {
        const existing = institutions.filter((i) => (i.cnpj || '').replace(/\D/g, '') === cnpj.replace(/\D/g, ''));
        if (existing.length > 0) {
          toast({ title: 'CNPJ já cadastrado', description: `Encontradas ${existing.length} instituições com este CNPJ. Cadastro permitido; visualização será consolidada.`, variant: 'default' });
        }
        const ref = await addInstitution({ name, category: category as Institution['category'], link, cnpj, legalName, contact: { email: contactEmail, phone: contactPhone } });
        try {
          const actorEmail = auth.currentUser?.email || '';
          const actorUid = auth.currentUser?.uid || '';
          await logAdminAction({ actorUid, actorEmail, action: 'create', entity: 'institution', entityId: (ref as any).id, entityName: name, dateTime: new Date().toISOString() });
        } catch { }
        const cnpjEmail = `cnpj-${cnpj.replace(/\D/g, '')}@instituicao.local`;
        try {
          const cred = await createUserWithEmailAndPassword(auth, cnpjEmail, instPwd);
          await updateProfile(cred.user, { displayName: name });
          await fsSetDoc(fsDoc(db, 'users', cred.user.uid), { role: 'instituicao', institutionId: (ref as any).id });
        } catch (e) {
          const code = (e as any)?.code;
          if (code === 'auth/email-already-in-use') {
            toast({ title: 'Conta já existente', description: 'A conta institucional para este CNPJ já existe. Se necessário, atualize a senha via servidor admin.', variant: 'default' });
          } else {
            toast({ title: 'Falha ao criar usuário institucional', description: 'Verifique as configurações de autenticação.', variant: 'destructive' });
          }
        }
        if (responsibleEmail) {
          try {
            const { adminPost } = await import('@/services/admin-api');
            const pass = instPwd || 'Teste@1234';
            const resp = await adminPost('/register-institution', { email: responsibleEmail, password: pass, displayName: name });
            if (resp.ok) {
              await adminPost('/link-user', { email: responsibleEmail, role: 'instituicao', institutionId: (ref as any).id });
              toast({ title: 'Responsável vinculado', description: `Acesso criado para ${responsibleEmail}.` });
            }
          } catch { }
        }
        const updated = await listInstitutions();
        setInstitutions(updated);
        setInstPwd(''); setInstPwdConfirm('');
      } catch {
        toast({ title: 'Erro ao cadastrar instituição', description: 'Tente novamente mais tarde.', variant: 'destructive' });
        return;
      }
      toast({
        title: "Instituição cadastrada",
        description: `${name} foi adicionada com sucesso.`,
      });
    }
  };

  // ===== Importação de Instituições via CSV =====
  const splitCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        // Se estamos dentro de aspas e o próximo caractere é aspas, é escape
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // pula o escape
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result.map((s) => s.trim());
  };


  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCsvFile(file);
  };

  const handleImportCsv = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) {
      toast({ title: 'Selecione um arquivo', description: 'Escolha um arquivo .csv para importar.', variant: 'destructive' });
      return;
    }
    let text = '';
    try {
      text = await csvFile.text();
    } catch {
      toast({ title: 'Erro ao ler arquivo', description: 'Não foi possível ler o CSV selecionado.', variant: 'destructive' });
      return;
    }
    let parsed: { rows: InstitutionCsvRow[]; errors: string[]; legacy: boolean } = { rows: [], errors: [], legacy: false };
    try {
      parsed = parseInstitutionsCsv(text);
    } catch (err: any) {
      toast({ title: 'CSV inválido', description: err?.message || 'Verifique o cabeçalho e o conteúdo.', variant: 'destructive' });
      return;
    }
    if (parsed.rows.length === 0) {
      toast({ title: 'Nenhuma linha válida', description: 'O arquivo CSV não contém linhas válidas.', variant: 'destructive' });
      return;
    }
    const progress = toast({ title: 'Importando instituições...', description: `Encontradas ${parsed.rows.length} linhas. Iniciando importação.`, duration: 1000000 });
    let ok = 0;
    let fail = 0;
    for (const r of parsed.rows) {
      try {
        await addInstitution({
          name: r.name,
          category: r.category as Institution['category'],
          link: r.link,
          cnpj: r.cnpj || '',
          legalName: r.legalName || undefined,
          contact: { email: r.contactEmail || undefined, phone: r.contactPhone || undefined },
          logoUrl: r.logoUrl || undefined,
          social: r.social,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    try {
      const updated = await listInstitutions();
      setInstitutions(updated);
    } catch { }
    const extra = parsed.errors.length > 0 ? `, avisos: ${parsed.errors.length}` : '';
    progress.update({ title: 'Importação concluída', description: `${ok} adicionadas, ${fail} falhas${extra}.`, duration: 5000 });
    if (parsed.errors.length > 0) {
      const detail = parsed.errors.slice(0, 6).join('\n');
      toast({ title: 'Relatório de avisos do CSV', description: detail, variant: 'outline' });
    }
    setCsvFile(null);
  };

  const handleAddCouncilor = async (formData: FormData) => {
    const name = formData.get('councilorName') as string;
    const party = formData.get('party') as string;
    const totalLimit = formData.get('totalLimit') as string;
    const photoUrl = formData.get('photoUrl') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string; // usado pelo script admin, não armazenado

    // Remove formatação monetária para obter valor numérico
    const numericLimit = parseInt(totalLimit.replace(/\D/g, '')) || 0;

    if (name && party && numericLimit && email && password) {
      try {
        const ref = await addCouncilor({ name, party, totalLimit: numericLimit, photoUrl: photoUrl || '/src/assets/parlamentar-photo.jpg', email });
        const updated = await listCouncilors();
        setCouncilors(updated);
        toast({ title: 'Vereador cadastrado', description: `${name} foi adicionado com sucesso.` });

        // Chama servidor admin local para criar usuário no Auth (role: vereador)
        try {
          const { adminPost } = await import('@/services/admin-api');
          const resp = await adminPost('/register-councilor', {
            email,
            password,
            displayName: name,
            councilorId: ref.id,
          });
          if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${resp.status}`);
          }
          const data = await resp.json();
          toast({ title: 'Conta de acesso criada', description: `Usuário Auth criado para ${email}.`, variant: 'default' });
        } catch (e) {
          toast({ title: 'Servidor admin não disponível', description: 'Os dados do vereador foram salvos no Firestore, mas a conta no Auth não foi criada. Inicie o servidor admin com "npm run admin:server" e tente novamente.', variant: 'destructive' });
        }
      } catch {
        toast({ title: 'Erro ao cadastrar vereador', description: 'Tente novamente mais tarde.', variant: 'destructive' });
      }
    }
  };

  const toggleAllocations = async () => {
    const newStatus = !allocationsLocked;
    setAllocationsLocked(newStatus);
    try { await setSettings({ allocationsLocked: newStatus, lockSchedule }); } catch { }
    toast({
      title: newStatus ? "Alocações bloqueadas" : "Alocações liberadas",
      description: newStatus
        ? "Vereadores não poderão mais modificar emendas"
        : "Vereadores podem modificar emendas novamente",
    });
  };

  const setInstitutionSchedule = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const startDate = String(fd.get('instStartDate') || '');
    const startTime = String(fd.get('instStartTime') || '');
    const endDate = String(fd.get('instEndDate') || '');
    const endTime = String(fd.get('instEndTime') || '');
    const notify = !!fd.get('instNotify');
    const completed = !!fd.get('instCompleted');
    if (!startDate || !startTime || !endDate || !endTime) {
      toast({ title: 'Campos obrigatórios', description: 'Informe início e término do agendamento.', variant: 'destructive' });
      return;
    }
    const start = new Date(`${startDate}T${startTime}:00`).getTime();
    const end = new Date(`${endDate}T${endTime}:00`).getTime();
    if (!(start < end)) {
      toast({ title: 'Período inválido', description: 'Término deve ser posterior ao início.', variant: 'destructive' });
      return;
    }
    if (lockSchedule.enabled && lockSchedule.date && lockSchedule.time) {
      const lockStart = new Date(`${lockSchedule.date}T${lockSchedule.time}:00`).getTime();
      const lockEnd = lockStart + 60 * 60 * 1000;
      const overlaps = start < lockEnd && end > lockStart;
      if (overlaps) {
        toast({ title: 'Conflito de agendamento', description: 'Período das instituições conflita com o bloqueio dos vereadores.', variant: 'destructive' });
        return;
      }
    }
    const next = { enabled: true, startDate, startTime, endDate, endTime, notify, completed };
    setInstFormSchedule(next);
    try { await setSettings({ allocationsLocked, lockSchedule, institutionFormSchedule: next }); } catch { }
    toast({ title: 'Agendamento definido', description: 'Período para respostas das instituições configurado.' });
  };

  const handleScheduleLock = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const date = formData.get('lockDate') as string;
    const time = formData.get('lockTime') as string;

    if (date && time) {
      const next = { enabled: true, date, time };
      setLockSchedule(next);
      try { await setSettings({ allocationsLocked, lockSchedule: next }); } catch { }

      toast({
        title: "Bloqueio agendado",
        description: `As alocações serão bloqueadas em ${formatDateTimeLabel(date, time)}`,
      });
    }
  };

  // Executa bloqueio automaticamente quando atingir o horário agendado (fuso Cuiabá)
  useEffect(() => {
    let timer: number | undefined;
    const shouldWatch = lockSchedule.enabled && !!lockSchedule.date && !!lockSchedule.time && !allocationsLocked;
    const check = async () => {
      if (isNowPastScheduleCuiaba(lockSchedule.date, lockSchedule.time)) {
        setAllocationsLocked(true);
        const next = { ...lockSchedule, enabled: false };
        setLockSchedule(next);
        try { await setSettings({ allocationsLocked: true, lockSchedule: next }); } catch { }
        toast({
          title: 'Alocações bloqueadas automaticamente',
          description: `Bloqueio executado em ${formatDateTimeLabel(lockSchedule.date, lockSchedule.time)}`,
        });
      }
    };
    if (shouldWatch) {
      // Checa imediatamente e depois em intervalos curtos
      check();
      timer = window.setInterval(check, 15000);
    }
    return () => { if (timer) window.clearInterval(timer); };
  }, [lockSchedule, allocationsLocked, toast]);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <NavigationHeader userType="admin" userName={localStorage.getItem('userName') || 'Administrador do Sistema'} />

      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <Tabs defaultValue="institutions" className="relative">
          {/* Toggle para mobile */}
          <div className="md:hidden mb-4 flex items-center justify-start">
            <Button
              variant="outline"
              onClick={() => setIsSidebarOpen(true)}
              aria-controls="admin-sidebar"
              aria-expanded={isSidebarOpen}
              aria-label="Abrir menu administrativo"
            >
              <i className="fas fa-bars mr-2"></i>
              Menu
            </Button>
          </div>

          {/* Overlay mobile */}
          {isSidebarOpen && (
            <button
              type="button"
              className="fixed inset-0 z-20 bg-black/40 md:hidden"
              aria-label="Fechar menu"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          <div className="md:grid md:grid-cols-[280px_minmax(0,1fr)] md:gap-6">
            {/* Sidebar fixa/colapsável */}
            <aside
              id="admin-sidebar"
              role="complementary"
              aria-label="Navegação administrativa"
              className={`fixed top-0 left-0 h-full w-[280px] bg-card border-r border-border z-30 transition-transform duration-300 ease-in-out transform md:static md:h-auto md:bg-transparent md:border-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
            >
              {/* Conteúdo da sidebar */}
              <div className="p-4 sm:p-6 md:p-0">
                {/* Header movido para a sidebar, com hierarquia e ícone destacado */}
                <div className="mb-6 md:mb-8">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg border bg-primary/10 text-primary border-primary/20">
                      <i className="fas fa-user-shield text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-foreground">Administração</h2>
                      <p className="text-xs md:text-sm leading-relaxed text-muted-foreground">Gerencie vereadores, instituições e configurações do sistema</p>
                    </div>
                  </div>
                </div>

                {/* Botão de fechar (mobile) */}
                <div className="md:hidden mb-4">
                  <Button
                    variant="ghost"
                    onClick={() => setIsSidebarOpen(false)}
                    aria-label="Fechar menu administrativo"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Fechar
                  </Button>
                </div>

                {/* Navegação com título e espaçamentos consistentes */}
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Navegação</p>
                <nav aria-label="Seções administrativas">
                  <TabsList className="flex flex-col gap-1 h-auto">
                    <TabsTrigger value="institutions" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-landmark mr-2"></i>
                      Instituições
                    </TabsTrigger>
                    <TabsTrigger value="apiTokens" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-code mr-2"></i>
                      Gestão de API
                    </TabsTrigger>
                    <TabsTrigger value="amendmentsTable" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-table mr-2"></i>
                      Administração de Emendas
                    </TabsTrigger>
                    <TabsTrigger value="objectDetails" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-clipboard-list mr-2"></i>
                      Detalhamento do Objeto
                    </TabsTrigger>
                    <TabsTrigger value="councilors" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-users mr-2"></i>
                      Vereadores
                    </TabsTrigger>
                    <TabsTrigger value="settings" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-cog mr-2"></i>
                      Configurações
                    </TabsTrigger>
                    <TabsTrigger value="templates" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-file-alt mr-2"></i>
                      Modelos de Descrição
                    </TabsTrigger>
                    <TabsTrigger value="admins" className="w-full text-left justify-start px-3 py-2 text-sm font-medium rounded-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary hover:bg-muted transition-colors">
                      <i className="fas fa-user-shield mr-2"></i>
                      Administradores
                    </TabsTrigger>
                  </TabsList>
                </nav>
              </div>
            </aside>

            {/* Conteúdo principal */}
            <main className="md:ml-0">

              <TabsContent value="institutions" className="space-y-6">
                {consolidatedOpen && consolidatedView && (
                  <div className="p-4 border rounded bg-muted" role="dialog" aria-label="Consolidação por CNPJ">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">Consolidado • CNPJ: {consolidatedView.cnpj}</p>
                        <p className="text-sm text-muted-foreground">Instituições agrupadas: {consolidatedView.ids.length}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            const header = ['Descrição', 'Valor', 'Data', 'InstituiçãoId'];
                            const rows = consolidatedView.items.map(a => [a.description || '', String(a.value || 0), a.date || '', a.institutionId]);
                            const csv = [header.join(','), ...rows.map(r => r.map(cell => (cell.includes(',') || cell.includes('"')) ? `"${cell.replace(/"/g, '""')}"` : cell).join(','))].join('\n');
                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url; link.download = `consolidado-${consolidatedView.cnpj.replace(/\D/g, '')}.csv`; link.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          <i className="fas fa-file-csv mr-2"></i>
                          Exportar CSV
                        </Button>
                        <Button
                          onClick={() => {
                            const w = window.open('', '_blank');
                            if (!w) return;
                            w.document.write('<html><head><title>Consolidado</title></head><body>');
                            w.document.write(`<h3>Consolidado • CNPJ: ${consolidatedView.cnpj}</h3>`);
                            w.document.write('<table border="1" cellspacing="0" cellpadding="4">');
                            w.document.write('<thead><tr><th>Descrição</th><th>Valor</th><th>Data</th><th>InstituiçãoId</th></tr></thead><tbody>');
                            consolidatedView.items.forEach(a => {
                              w.document.write(`<tr><td>${(a.description || '').replace(/</g, '&lt;')}</td><td>${(a.value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td><td>${a.date || ''}</td><td>${a.institutionId}</td></tr>`);
                            });
                            w.document.write('</tbody></table>');
                            w.document.write('</body></html>');
                            w.document.close();
                            w.focus();
                            w.print();
                          }}
                        >
                          <i className="fas fa-file-pdf mr-2"></i>
                          Exportar PDF
                        </Button>
                        <Button variant="ghost" onClick={() => { setConsolidatedOpen(false); setConsolidatedView(null); }}>Fechar</Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 pr-4">Descrição</th>
                            <th className="text-right py-2 pr-4">Valor</th>
                            <th className="text-left py-2">Data</th>
                            <th className="text-left py-2">InstituiçãoId</th>
                          </tr>
                        </thead>
                        <tbody>
                          {consolidatedView.items.map((a) => (
                            <tr key={a.id} className="border-b">
                              <td className="py-2 pr-4">{a.description}</td>
                              <td className="py-2 pr-4 text-right">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(a.value || 0)}</td>
                              <td className="py-2">{a.date}</td>
                              <td className="py-2">{a.institutionId}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle>Cadastrar Nova Instituição</CardTitle>
                    <CardDescription>
                      Adicione instituições que podem receber emendas dos vereadores
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleAddInstitution(formData);
                      e.currentTarget.reset();
                    }} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="institutionName">Nome da Instituição</Label>
                          <Input
                            id="institutionName"
                            name="institutionName"
                            placeholder="Ex: Hospital Municipal..."
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="cnpj">CNPJ</Label>
                          <Input
                            id="cnpj"
                            name="cnpj"
                            placeholder="00.000.000/0000-00"
                            required
                            onChange={(e) => { e.currentTarget.value = formatCnpj(e.currentTarget.value); }}
                            aria-label="CNPJ da instituição"
                          />
                        </div>
                        <div>
                          <Label htmlFor="legalName">Razão Social (opcional)</Label>
                          <Input id="legalName" name="legalName" placeholder="Razão Social" />
                        </div>
                        <div>
                          <Label htmlFor="contactEmail">Contato (email)</Label>
                          <Input id="contactEmail" name="contactEmail" type="email" placeholder="email@exemplo.com" />
                        </div>
                        <div>
                          <Label htmlFor="contactPhone">Contato (telefone)</Label>
                          <Input id="contactPhone" name="contactPhone" placeholder="(00) 00000-0000" />
                        </div>
                        <div>
                          <Label htmlFor="responsibleEmail">Usuário administrador responsável (opcional)</Label>
                          <Input id="responsibleEmail" name="responsibleEmail" type="email" placeholder="email do responsável" />
                        </div>
                        <div className="col-span-1 md:col-span-2">
                          <Label htmlFor="instPwd">Senha de acesso institucional</Label>
                          <div className="flex gap-2">
                            <Input id="instPwd" name="instPwd" type={instPwdShow ? 'text' : 'password'} placeholder="Defina a senha" value={instPwd} onChange={(e) => setInstPwd(e.target.value)} aria-label="Senha institucional" />
                            <Button type="button" variant="outline" onClick={() => setInstPwdShow((v) => !v)} aria-label="Mostrar/ocultar senha">{instPwdShow ? 'Ocultar' : 'Mostrar'}</Button>
                          </div>
                          <div className="mt-2">
                            <Label htmlFor="instPwdConfirm">Confirmar senha</Label>
                            <Input id="instPwdConfirm" name="instPwdConfirm" type={instPwdShow ? 'text' : 'password'} placeholder="Confirme a senha" value={instPwdConfirm} onChange={(e) => setInstPwdConfirm(e.target.value)} aria-label="Confirmar senha" />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Mínimo 8 caracteres, letras e números.</p>
                        </div>
                        <div>
                          <Label htmlFor="category">Categoria</Label>
                          <Select name="category" required>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a categoria" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="SAÚDE">SAÚDE</SelectItem>
                              <SelectItem value="ASSISTÊNCIA SOCIAL">ASSISTÊNCIA SOCIAL</SelectItem>
                              <SelectItem value="EDUCAÇÃO">EDUCAÇÃO</SelectItem>
                              <SelectItem value="ESPORTE">ESPORTE</SelectItem>
                              <SelectItem value="CULTURA">CULTURA</SelectItem>
                              <SelectItem value="AGRICULTURA">AGRICULTURA</SelectItem>
                              <SelectItem value="SEGURANÇA E TRÂNSITO">SEGURANÇA E TRÂNSITO</SelectItem>
                              <SelectItem value="DEMAIS AÇÕES">DEMAIS AÇÕES</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="link">Link da Instituição</Label>
                        <Input
                          id="link"
                          name="link"
                          type="url"
                          placeholder="https://exemplo.com.br"
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        <i className="fas fa-plus mr-2"></i>
                        Cadastrar Instituição
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* <Card>
                  <CardHeader>
                    <CardTitle>Credenciais de Teste para Instituição</CardTitle>
                    <CardDescription>Gere um usuário de teste com papel institucional.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const email = 'instituicao_teste@email.com';
                      const password = 'Teste@1234';
                      try {
                        const { adminPost } = await import('@/services/admin-api');
                        const resp = await adminPost('/register-institution', {
                          email, password, displayName: 'Instituição Teste'
                        });
                        if (!resp.ok) throw new Error('Falha ao criar usuário institucional');
                        // opcional: vincular a primeira instituição existente
                        const first = institutions[0];
                        if (first) {
                          try {
                            const linkResp = await adminPost('/link-user', { email, role: 'instituicao', institutionId: first.id });
                            if (!linkResp.ok) console.warn('Falha ao vincular usuário à instituição');
                          } catch { }
                        }
                        toast({ title: 'Usuário institucional de teste criado', description: `${email} / ${password}` });
                      } catch (err) {
                        toast({ title: 'Erro', description: (err as Error).message, variant: 'destructive' });
                      }
                    }}>
                      <div className="text-sm text-muted-foreground mb-2">Será criado: instituicao_teste@email.com / Teste@1234</div>
                      <Button type="submit" variant="outline" className="w-full">
                        <i className="fas fa-user-plus mr-2"></i>
                        Criar credenciais de teste
                      </Button>
                    </form>
                  </CardContent>
                </Card> */}

                <Card>
                  <CardHeader>
                    <CardTitle>Importar Instituições via CSV</CardTitle>
                    <CardDescription>
                      Faça upload de um arquivo CSV no novo formato: <code className="whitespace-pre-wrap break-words">nome,categoria,link,cnpj,legal_name,contact_email,contact_phone,logo_url,social_facebook,social_instagram,social_x,social_tiktok,social_youtube,social_whatsapp,responsible_email</code>.
                      O campo <code>cnpj</code> é obrigatório no novo formato. Arquivos do formato antigo (<code>nome,categoria,link</code>) continuam aceitos em modo legado.
                      Baixe o modelo: <a href="/templates/institutions-template.csv" className="text-primary hover:underline" download>institutions-template.csv</a>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleImportCsv} className="space-y-4">
                      <div>
                        <Label htmlFor="institutionsCsv">Arquivo CSV</Label>
                        <Input id="institutionsCsv" type="file" accept=".csv" onChange={handleCsvFileChange} />
                        <p className="text-xs text-muted-foreground mt-1">
                          Dica: use as categorias exatamente como: SAÚDE, ASSISTÊNCIA SOCIAL, EDUCAÇÃO, ESPORTE, CULTURA, AGRICULTURA, SEGURANÇA E TRÂNSITO, DEMAIS AÇÕES.
                          No novo formato, <code>cnpj</code> deve ser válido (14 dígitos) e os campos de contato/redes são opcionais.
                        </p>
                      </div>
                      <Button type="submit" disabled={!csvFile} className="w-full">
                        <i className="fas fa-file-import mr-2"></i>
                        Importar CSV
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Instituições Cadastradas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="relative w-full md:w-80">
                          <Input
                            value={institutionQuery}
                            onChange={(e) => setInstitutionQuery(e.target.value)}
                            placeholder="Buscar por nome, CNPJ ou código"
                            aria-label="Buscar instituições"
                          />
                          <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"></i>
                        </div>
                        {institutionQuery && (
                          <Button variant="ghost" onClick={() => setInstitutionQuery('')} aria-label="Limpar busca">Limpar</Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedInstitutionIds.size === filteredInstitutions.length && filteredInstitutions.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedInstitutionIds(new Set(filteredInstitutions.map((i) => i.id)));
                            } else {
                              setSelectedInstitutionIds(new Set());
                            }
                          }}
                          aria-label="Selecionar todas as instituições"
                        />
                        <span className="text-sm text-muted-foreground">Selecionar tudo</span>
                      </div>
                      <Button
                        variant="destructive"
                        disabled={selectedInstitutionIds.size === 0}
                        onClick={async () => {
                          if (selectedInstitutionIds.size === 0) return;
                          const ids = Array.from(selectedInstitutionIds);
                          try {
                            await Promise.all(ids.map((id) => deleteInstitution(id)));
                            const updated = await listInstitutions();
                            setInstitutions(updated);
                            setSelectedInstitutionIds(new Set());
                            toast({ title: 'Instituições excluídas', description: `${ids.length} instituições removidas.` });
                          } catch {
                            toast({ title: 'Erro ao excluir em lote', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                          }
                        }}
                      >
                        <i className="fas fa-trash mr-2"></i>
                        Excluir Selecionados
                      </Button>
                    </div>
                    <div className="space-y-3" aria-label="Lista de grupos por CNPJ">
                      {groupsByCnpj.map((g) => (
                        <div key={g.key} className="border rounded-lg">
                          <button
                            type="button"
                            className="w-full flex items-center justify-between px-3 py-2"
                            aria-expanded={!!openGroups[g.key]}
                            onClick={() => setOpenGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{g.label}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{g.items.length}</span>
                            </div>
                          </button>
                          <div className={`space-y-3 p-3 ${openGroups[g.key] ? '' : 'hidden'}`}>
                            {g.items.map((i) => (
                              <div key={i.id} className="p-3 bg-muted rounded-lg">
                                {editingInstitutionId === i.id ? (
                                  <form
                                    onSubmit={async (e) => {
                                      e.preventDefault();
                                      try {
                                        if (!validateCnpj(editInstitutionValues.cnpj)) {
                                          toast({ title: 'CNPJ inválido', description: 'Verifique o número do CNPJ.', variant: 'destructive' });
                                          return;
                                        }
                                        if (editInstPwd || editInstPwdConfirm) {
                                          if (editInstPwd !== editInstPwdConfirm) {
                                            toast({ title: 'Senhas diferentes', description: 'Confirme a senha corretamente.', variant: 'destructive' });
                                            return;
                                          }
                                          if (!isStrongPassword(editInstPwd)) {
                                            toast({ title: 'Senha fraca', description: 'A senha deve ter ao menos 8 caracteres, letras e números.', variant: 'destructive' });
                                            return;
                                          }
                                        }
                                        await updateInstitution(i.id, { name: editInstitutionValues.name, category: editInstitutionValues.category, link: editInstitutionValues.link, cnpj: editInstitutionValues.cnpj, legalName: editInstitutionValues.legalName, contact: { email: editInstitutionValues.contactEmail, phone: editInstitutionValues.contactPhone } });
                                        if (editInstPwd) {
                                          const cnpjEmail = `cnpj-${editInstitutionValues.cnpj.replace(/\D/g, '')}@instituicao.local`;
                                          try {
                                            const cred = await createUserWithEmailAndPassword(auth, cnpjEmail, editInstPwd);
                                            await updateProfile(cred.user, { displayName: editInstitutionValues.name });
                                            await fsSetDoc(fsDoc(db, 'users', cred.user.uid), { role: 'instituicao', institutionId: i.id });
                                            toast({ title: 'Acesso institucional criado', description: `Conta criada para ${cnpjEmail}.` });
                                          } catch (e) {
                                            const code = (e as any)?.code;
                                            if (code === 'auth/email-already-in-use') {
                                              toast({ title: 'Conta já existente', description: `A conta institucional para este CNPJ já existe. A senha não pode ser alterada sem servidor admin.`, variant: 'default' });
                                            } else {
                                              toast({ title: 'Falha ao criar acesso institucional', description: 'Verifique as configurações de autenticação.', variant: 'destructive' });
                                            }
                                          }
                                        }
                                        if (editInstitutionValues.responsibleEmail) {
                                          try {
                                            const { adminPost } = await import('@/services/admin-api');
                                            const pass = editInstPwd || 'Teste@1234';
                                            const resp = await adminPost('/register-institution', { email: editInstitutionValues.responsibleEmail, password: pass, displayName: editInstitutionValues.name });
                                            if (resp.ok) {
                                              await adminPost('/link-user', { email: editInstitutionValues.responsibleEmail, role: 'instituicao', institutionId: i.id });
                                              toast({ title: 'Responsável vinculado', description: `Acesso criado para ${editInstitutionValues.responsibleEmail}.` });
                                            }
                                          } catch { }
                                        }
                                        const updated = await listInstitutions();
                                        setInstitutions(updated);
                                        setEditInstPwd(''); setEditInstPwdConfirm('');
                                        setEditingInstitutionId(null);
                                        toast({ title: 'Instituição atualizada', description: `${editInstitutionValues.name} salva com sucesso.` });
                                      } catch {
                                        toast({ title: 'Erro ao atualizar', description: 'Não foi possível salvar as alterações.', variant: 'destructive' });
                                      }
                                    }}
                                    className="space-y-3"
                                  >
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                      <div>
                                        <Label htmlFor={`inst-name-${i.id}`}>Nome</Label>
                                        <Input
                                          id={`inst-name-${i.id}`}
                                          value={editInstitutionValues.name}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, name: ev.target.value }))}
                                          required
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-cat-${i.id}`}>Categoria</Label>
                                        <Select
                                          value={editInstitutionValues.category}
                                          onValueChange={(val) => setEditInstitutionValues((v) => ({ ...v, category: val as Institution['category'] }))}
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Selecione a categoria" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="SAÚDE">SAÚDE</SelectItem>
                                            <SelectItem value="ASSISTÊNCIA SOCIAL">ASSISTÊNCIA SOCIAL</SelectItem>
                                            <SelectItem value="EDUCAÇÃO">EDUCAÇÃO</SelectItem>
                                            <SelectItem value="ESPORTE">ESPORTE</SelectItem>
                                            <SelectItem value="CULTURA">CULTURA</SelectItem>
                                            <SelectItem value="AGRICULTURA">AGRICULTURA</SelectItem>
                                            <SelectItem value="SEGURANÇA E TRÂNSITO">SEGURANÇA E TRÂNSITO</SelectItem>
                                            <SelectItem value="DEMAIS AÇÕES">DEMAIS AÇÕES</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-link-${i.id}`}>Link</Label>
                                        <Input
                                          id={`inst-link-${i.id}`}
                                          type="url"
                                          value={editInstitutionValues.link}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, link: ev.target.value }))}
                                          required
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-cnpj-${i.id}`}>CNPJ</Label>
                                        <Input
                                          id={`inst-cnpj-${i.id}`}
                                          value={editInstitutionValues.cnpj}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, cnpj: formatCnpj(ev.target.value) }))}
                                          placeholder="00.000.000/0000-00"
                                          required
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-legal-${i.id}`}>Razão Social (opcional)</Label>
                                        <Input
                                          id={`inst-legal-${i.id}`}
                                          value={editInstitutionValues.legalName || ''}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, legalName: ev.target.value }))}
                                          placeholder="Razão Social"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-contact-email-${i.id}`}>Contato (email)</Label>
                                        <Input
                                          id={`inst-contact-email-${i.id}`}
                                          type="email"
                                          value={editInstitutionValues.contactEmail || ''}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, contactEmail: ev.target.value }))}
                                          placeholder="email@exemplo.com"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-contact-phone-${i.id}`}>Contato (telefone)</Label>
                                        <Input
                                          id={`inst-contact-phone-${i.id}`}
                                          value={editInstitutionValues.contactPhone || ''}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, contactPhone: ev.target.value }))}
                                          placeholder="(00) 00000-0000"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`inst-resp-email-${i.id}`}>Usuário administrador responsável (opcional)</Label>
                                        <Input
                                          id={`inst-resp-email-${i.id}`}
                                          type="email"
                                          value={editInstitutionValues.responsibleEmail || ''}
                                          onChange={(ev) => setEditInstitutionValues((v) => ({ ...v, responsibleEmail: ev.target.value }))}
                                          placeholder="email do responsável"
                                        />
                                      </div>
                                      <div className="md:col-span-3">
                                        <Label htmlFor={`inst-pwd-${i.id}`}>Senha de acesso institucional</Label>
                                        <div className="flex gap-2">
                                          <Input id={`inst-pwd-${i.id}`} type={editInstPwdShow ? 'text' : 'password'} value={editInstPwd} onChange={(e) => setEditInstPwd(e.target.value)} placeholder="Defina a senha" aria-label="Senha institucional" />
                                          <Button type="button" variant="outline" onClick={() => setEditInstPwdShow((v) => !v)} aria-label="Mostrar/ocultar senha">{editInstPwdShow ? 'Ocultar' : 'Mostrar'}</Button>
                                        </div>
                                        <div className="mt-2">
                                          <Label htmlFor={`inst-pwd-confirm-${i.id}`}>Confirmar senha</Label>
                                          <Input id={`inst-pwd-confirm-${i.id}`} type={editInstPwdShow ? 'text' : 'password'} value={editInstPwdConfirm} onChange={(e) => setEditInstPwdConfirm(e.target.value)} placeholder="Confirme a senha" aria-label="Confirmar senha" />
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">Mínimo 8 caracteres, letras e números.</p>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 justify-end">
                                      <Button type="button" variant="outline" onClick={() => setEditingInstitutionId(null)}>Cancelar</Button>
                                      <Button type="submit">Salvar</Button>
                                      <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={async () => {
                                          try {
                                            const newPwd = editInstPwd.trim();
                                            const confirm = editInstPwdConfirm.trim();
                                            if (!newPwd || !confirm) {
                                              toast({ title: 'Informe a nova senha', description: 'Preencha os campos de senha e confirmação.', variant: 'destructive' });
                                              return;
                                            }
                                            if (newPwd !== confirm) {
                                              toast({ title: 'Senhas diferentes', description: 'Confirme a senha corretamente.', variant: 'destructive' });
                                              return;
                                            }
                                            if (!isStrongPassword(newPwd)) {
                                              toast({ title: 'Senha fraca', description: 'A senha deve ter ao menos 8 caracteres, letras e números.', variant: 'destructive' });
                                              return;
                                            }
                                            const masterPassword = adminMasterPassword.trim();
                                            if (!masterPassword) {
                                              toast({ title: 'Senha mestra obrigatória', description: 'Informe a senha mestra nas ações administrativas.', variant: 'destructive' });
                                              return;
                                            }
                                            const actorEmail = auth.currentUser?.email || localStorage.getItem('userEmail') || '';
                                            const actorUid = auth.currentUser?.uid || '';
                                            const cnpjDigits = editInstitutionValues.cnpj.replace(/\D/g, '');
                                            const body = { cnpj: cnpjDigits, newPassword: newPwd, actorEmail, actorUid, masterPassword };
                                            const resp = await adminPost('/reset-password-instituicao', body);
                                            if (!resp.ok) {
                                              const err = await resp.json().catch(() => ({}));
                                              const code = err.error || `HTTP ${resp.status}`;
                                              const msg = code === 'invalid-master-password'
                                                ? 'Senha mestra inválida.'
                                                : code === 'ip-not-allowed'
                                                  ? 'Seu IP não está autorizado para esta ação.'
                                                  : code === 'auth user with email' || code === 'institution with cnpj' ? 'Usuário institucional não encontrado.'
                                                    : 'Falha ao alterar senha.';
                                              throw new Error(msg);
                                            }
                                            toast({ title: 'Senha alterada', description: `Senha redefinida para a instituição (${formatCnpj(cnpjDigits)}).` });
                                            setEditInstPwd('');
                                            setEditInstPwdConfirm('');
                                          } catch (e) {
                                            toast({ title: 'Erro', description: (e as Error).message, variant: 'destructive' });
                                          }
                                        }}
                                      >
                                        Alterar senha (servidor)
                                      </Button>
                                    </div>
                                  </form>
                                ) : (
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <Checkbox
                                        checked={selectedInstitutionIds.has(i.id)}
                                        onCheckedChange={(checked) => {
                                          setSelectedInstitutionIds((prev) => {
                                            const next = new Set(prev);
                                            if (checked) next.add(i.id); else next.delete(i.id);
                                            return next;
                                          });
                                        }}
                                        aria-label={`Selecionar ${i.name}`}
                                      />
                                      <div>
                                        <p className="font-medium">{i.name}</p>
                                        <p className="text-sm text-muted-foreground capitalize">{i.category}</p>
                                        <a href={i.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                                          {i.link}
                                        </a>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        onClick={() => {
                                          setEditingInstitutionId(i.id);
                                          setEditInstitutionValues({ name: i.name, category: i.category, link: i.link, cnpj: i.cnpj || '', legalName: i.legalName || '', contactEmail: i.contact?.email || '', contactPhone: i.contact?.phone || '', responsibleEmail: '' });
                                        }}
                                      >
                                        Editar
                                      </Button>
                                      {(() => {
                                        const same = institutions.filter((x) => (x.cnpj || '').replace(/\D/g, '') === (i.cnpj || '').replace(/\D/g, '') && x.id !== i.id);
                                        if (same.length === 0) return null;
                                        return (
                                          <Button
                                            variant="secondary"
                                            onClick={async () => {
                                              try {
                                                const ids = [i.id, ...same.map(s => s.id)];
                                                const all = await listAmendments();
                                                const grouped = all.filter(a => ids.includes(a.institutionId));
                                                const sum = grouped.reduce((acc, a) => acc + (a.value || 0), 0);
                                                setConsolidatedView({ ids, items: grouped, total: sum, cnpj: i.cnpj || '' });
                                                setConsolidatedOpen(true);
                                              } catch {
                                                toast({ title: 'Erro ao consolidar', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                                              }
                                            }}
                                          >
                                            Ver consolidado
                                          </Button>
                                        );
                                      })()}
                                      <Button
                                        variant="destructive"
                                        onClick={async () => {
                                          try {
                                            await deleteInstitution(i.id);
                                            const updated = await listInstitutions();
                                            setInstitutions(updated);
                                            setSelectedInstitutionIds((s) => {
                                              const next = new Set(s); next.delete(i.id); return next;
                                            });
                                            toast({ title: 'Instituição excluída', description: `${i.name} foi removida.` });
                                          } catch {
                                            toast({ title: 'Erro ao excluir instituição', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                                          }
                                        }}
                                      >
                                        Excluir
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ===== Tabela Administrativa de Emendas ===== */}
              <TabsContent value="amendmentsTable" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Administração de Emendas</CardTitle>
                    <CardDescription>Visualize, edite e exporte os dados de emendas parlamentares</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <AdminAmendmentsTable
                      amendments={amendments}
                      councilors={councilors}
                      institutions={institutions}
                      isAdminUser={isAdminUser}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="objectDetails" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Detalhamento do Objeto</CardTitle>
                    <CardDescription>Informações registradas pelas instituições. Ordenado pelo número da emenda.</CardDescription>
                    <div className="mt-2">
                      <Button variant="outline" onClick={() => {
                        const rows = [...amendments]
                          .map((a) => {
                            const oc = a.officialCode || '';
                            const parts = oc.split('-');
                            const seq = parts.length >= 3 ? parts[1] : '';
                            const aNum = parseInt(seq || '0', 10) || 0;
                            return { ...a, aNum };
                          })
                          .sort((x, y) => x.aNum - y.aNum);
                        exportCsvRows(rows);
                      }}>
                        <i className="fas fa-file-csv mr-2" />
                        Exportar CSV (lista)
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {[...amendments]
                        .map((a) => {
                          const oc = a.officialCode || '';
                          const parts = oc.split('-');
                          const seq = parts.length >= 3 ? parts[1] : '';
                          const aNum = parseInt(seq || '0', 10) || 0;
                          const inst = institutions.find((i) => i.id === a.institutionId);
                          const coun = councilors.find((c) => c.id === a.councilorId);
                          const hasDetail = !!(a.objectDetail && a.objectDetail.trim().length > 0);
                          const hasSchedule = instFormSchedule.enabled && instFormSchedule.startDate && instFormSchedule.startTime && instFormSchedule.endDate && instFormSchedule.endTime;
                          let badgeColor = 'bg-amber-200 text-amber-900';
                          let badgeText = 'Aguardando';
                          if (hasDetail) {
                            if (a.detailingSaved) {
                              badgeColor = 'bg-emerald-200 text-emerald-900';
                              badgeText = 'Formulário recebido';
                            } else {
                              badgeColor = 'bg-blue-200 text-blue-900';
                              badgeText = 'Rascunho (c/ conteúdo)';
                            }
                          }
                          else if (hasSchedule) {
                            const now = Date.now();
                            const s = new Date(`${instFormSchedule.startDate}T${instFormSchedule.startTime}:00`).getTime();
                            const e = new Date(`${instFormSchedule.endDate}T${instFormSchedule.endTime}:00`).getTime();
                            if (now > e) { badgeColor = 'bg-destructive text-destructive-foreground'; badgeText = 'Perdido'; }
                          }
                          const valueBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(a.value || 0);
                          const dateLabel = a.date ? new Date(a.date).toLocaleDateString('pt-BR') : '-';
                          const categoryLabel = a.category || inst?.category || '-';
                          const statusLabel = a.status || '-';
                          const workProject = a.workPlan?.projectName || '';
                          const attachmentsCount = Array.isArray(a.attachments) ? a.attachments.length : 0;
                          return { ...a, aNum, instName: inst?.name || '', hasDetail, badgeColor, badgeText, councilorName: coun?.name || '', valueBRL, dateLabel, categoryLabel, statusLabel, workProject, attachmentsCount };
                        })
                        .sort((x, y) => x.aNum - y.aNum)
                        .map((r) => (
                          <div
                            key={r.id}
                            className="grid grid-cols-12 gap-3 p-3 border rounded w-full overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ring-offset-background"
                            role="button"
                            tabIndex={0}
                            onClick={() => { setDetailViewItem(r); setDetailViewOpen(true); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailViewItem(r); setDetailViewOpen(true); } }}
                          >
                            {(() => {
                              const cNum = councilorCodeMap(councilors).get(r.councilorId) || '999';
                              const seq = String(r.aNum).padStart(3, '0');
                              const ident = buildIdentification(cNum, seq, r.date);
                              const label = r.officialCode || ident;
                              return (
                                <div className="col-span-6 md:col-span-2 font-mono truncate" title={label}>{label}</div>
                              );
                            })()}
                            <div className="col-span-6 md:col-span-3" title={r.instName}>{r.instName}</div>
                            <div className="col-span-12 md:col-span-5 min-w-0">
                              <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                                <div><span className="text-muted-foreground">Vereador:</span> <span className="font-medium truncate" title={r.councilorName}>{r.councilorName || '-'}</span></div>
                                <div><span className="text-muted-foreground">Valor:</span> <span className="font-bold">{r.valueBRL}</span></div>
                                <div><span className="text-muted-foreground">Categoria:</span> <span>{r.categoryLabel}</span></div>
                                <div><span className="text-muted-foreground">Data:</span> <span>{r.dateLabel}</span></div>
                                <div><span className="text-muted-foreground">Status:</span> <span>{r.statusLabel}</span></div>
                                {r.workProject && (<div className="col-span-2"><span className="text-muted-foreground">Projeto:</span> <span className="truncate" title={r.workProject}>{r.workProject}</span></div>)}
                              </div>
                              <div className="whitespace-pre-wrap break-words text-sm" title={r.objectDetail || '-'}>{r.objectDetail || '-'}</div>
                            </div>
                            <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-2">
                              {r.attachmentsCount > 0 && (
                                <span className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground">Anexos: {r.attachmentsCount}</span>
                              )}
                              <span className={`px-2 py-1 text-xs rounded ${r.badgeColor}`}>{r.badgeText}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
                <Dialog open={detailViewOpen} onOpenChange={(o) => setDetailViewOpen(o)}>
                  <DialogContent className="sm:max-w-[80vw] max-w-[95vw]" aria-labelledby="detalhe-emenda-titulo" aria-describedby="detalhe-emenda-desc">
                    <DialogHeader>
                      <DialogTitle id="detalhe-emenda-titulo">Detalhes da Emenda</DialogTitle>
                      <DialogDescription id="detalhe-emenda-desc">Visualização detalhada e exportação</DialogDescription>
                    </DialogHeader>
                    {detailViewItem && (
                      <div className="space-y-4">
                        {(() => {
                          const cNum = councilorCodeMap(councilors).get(detailViewItem.councilorId) || '999';
                          const seq = String(detailViewItem.aNum || parseInt(((detailViewItem.officialCode || '').split('-')[1] || '0'), 10) || 0).padStart(3, '0');
                          const ident = buildIdentification(cNum, seq, detailViewItem.date);
                          const label = detailViewItem.officialCode || ident;
                          const inst = institutions.find(i => i.id === detailViewItem.institutionId);
                          const cnpj = inst?.cnpj || '';
                          const signed = detailViewItem.signedLink || '';
                          const atts = Array.isArray(detailViewItem.attachments) ? detailViewItem.attachments : [];
                          const val = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(detailViewItem.value || 0);
                          const dateLabel = detailViewItem.date ? new Date(detailViewItem.date).toLocaleDateString('pt-BR') : '-';
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div><span className="text-muted-foreground">Número da Emenda:</span> <span className="font-mono">{label}</span></div>
                              <div><span className="text-muted-foreground">Parlamentar responsável:</span> <span>{detailViewItem.councilorName || '-'}</span></div>
                              <div><span className="text-muted-foreground">Categoria da emenda:</span> <span>{detailViewItem.categoryLabel || '-'}</span></div>
                              <div><span className="text-muted-foreground">Beneficiária (entidade):</span> <span>{inst?.name || '-'}</span></div>
                              <div><span className="text-muted-foreground">CNPJ da beneficiária:</span> <span>{cnpj || '-'}</span></div>
                              <div><span className="text-muted-foreground">Data:</span> <span>{dateLabel}</span></div>
                              <div><span className="text-muted-foreground">Classificação da emenda:</span> <span>{detailViewItem.classification || '-'}</span></div>
                              <div className="md:col-span-2"><span className="text-muted-foreground">Detalhamento do objeto:</span> <span className="whitespace-pre-wrap break-words">{detailViewItem.objectDetail || '-'}</span></div>
                              <div><span className="text-muted-foreground">Nome do projeto ou ação:</span> <span>{detailViewItem.workPlan?.projectName || '-'}</span></div>
                              <div><span className="text-muted-foreground">Valor total:</span> <span className="font-bold">{val}</span></div>
                              <div><span className="text-muted-foreground">Finalidade de uso:</span> <span>{detailViewItem.workPlan?.purpose || '-'}</span></div>
                              <div><span className="text-muted-foreground">Prazo de execução:</span> <span>{detailViewItem.workPlan?.period || '-'}</span></div>
                              <div className="md:col-span-2 flex items-center gap-2">
                                <span className="text-muted-foreground">Emenda:</span>
                                {signed ? (
                                  <>
                                    <span className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground">Assinada</span>
                                    <a href={signed} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">{signed}</a>
                                  </>
                                ) : (
                                  <span className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground">(sem emenda assinada)</span>
                                )}
                              </div>
                              <div className="md:col-span-2 flex items-center gap-2">
                                <span className="text-muted-foreground">Anexos:</span>
                                {atts && atts.length > 0 ? (
                                  <>
                                    <span className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground">{atts.length} anexo(s)</span>
                                    <span className="break-words">
                                      {atts.map((u: string, idx: number) => (
                                        <a key={idx} href={u} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all mr-2">{u}</a>
                                      ))}
                                    </span>
                                  </>
                                ) : (
                                  <span className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground">(sem anexo)</span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setDetailViewOpen(false)}>Fechar</Button>
                          <Button onClick={() => exportCsvRows([detailViewItem])} aria-label="Exportar CSV desta emenda">
                            <i className="fas fa-file-export mr-2" />
                            Exportar CSV
                          </Button>
                        </DialogFooter>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </TabsContent>

              <TabsContent value="admins" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Cadastrar Administrador</CardTitle>
                    <CardDescription>
                      Crie contas administrativas com confirmação por senha mestra.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form data-testid="admin-register-form" onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleRegisterAdmin(formData);
                      e.currentTarget.reset();
                    }} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="adminName">Nome</Label>
                          <Input id="adminName" name="adminName" placeholder="Nome do administrador" required />
                        </div>
                        <div>
                          <Label htmlFor="adminEmail">E-mail</Label>
                          <Input id="adminEmail" name="adminEmail" type="email" placeholder="admin@dominio.com" required />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="adminPassword">Senha</Label>
                          <Input id="adminPassword" name="adminPassword" type="password" placeholder="Defina uma senha forte" required />
                        </div>
                        <div>
                          <Label htmlFor="permissionLevel">Nível de Permissão</Label>
                          <Select name="permissionLevel" defaultValue="padrao">
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o nível" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="padrao">Administrador</SelectItem>
                              <SelectItem value="principal">Administrador Principal</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="adminInstitutionId">Instituição (opcional)</Label>
                        <Select value={registerAdminInstitutionId || 'none'} onValueChange={(v) => setRegisterAdminInstitutionId(v === 'none' ? '' : v)}>
                          <SelectTrigger id="adminInstitutionId"><SelectValue placeholder="Selecione uma instituição" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem vínculo</SelectItem>
                            {institutions.map((i) => (
                              <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input type="hidden" name="adminInstitutionId" value={registerAdminInstitutionId} />
                      </div>
                      <div>
                        <Label htmlFor="masterPassword">Senha Mestra</Label>
                        <Input id="masterPassword" name="masterPassword" type="password" placeholder="Confirme com a senha mestra" required />
                        <p className="text-xs text-muted-foreground mt-1">A ação é auditada e requer confirmação.</p>
                      </div>
                      <Button type="submit" className="w-full" disabled={isSubmittingAdmin}>
                        <i className="fas fa-user-shield mr-2"></i>
                        {isSubmittingAdmin ? 'Processando…' : 'Cadastrar Administrador'}
                      </Button>
                    </form>
                    {lastAdminCreation && (
                      <div className="mt-6 p-4 border rounded-lg bg-muted">
                        <p className="font-medium mb-2">Resultado da criação</p>
                        <p className="text-sm text-muted-foreground">E-mail: {lastAdminCreation.email}</p>
                        {lastAdminCreation.uid && (
                          <p className="text-sm text-muted-foreground">UID: {lastAdminCreation.uid}</p>
                        )}
                        {lastAdminCreation.verificationLink ? (
                          <div className="mt-2 flex items-center gap-2">
                            <a
                              href={lastAdminCreation.verificationLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline truncate max-w-full"
                              title={lastAdminCreation.verificationLink}
                            >
                              {lastAdminCreation.verificationLink}
                            </a>
                            <Button
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(lastAdminCreation.verificationLink!);
                                  toast({ title: 'Link copiado', description: 'O link de verificação foi copiado para a área de transferência.' });
                                } catch {
                                  toast({ title: 'Falha ao copiar', description: 'Não foi possível copiar o link.', variant: 'destructive' });
                                }
                              }}
                            >
                              Copiar link
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-2">Nenhum link de verificação foi gerado.</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Administradores Cadastrados</CardTitle>
                    <CardDescription>Lista de contas com acesso administrativo</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <Label htmlFor="adminActionsMasterPassword">Senha Mestra para Ações</Label>
                      <Input
                        id="adminActionsMasterPassword"
                        type="password"
                        placeholder="Digite a senha mestra"
                        value={adminMasterPassword}
                        onChange={(e) => setAdminMasterPassword(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Obrigatória para alterar nível e revogar acesso.</p>
                    </div>
                    <div className="space-y-2">
                      {admins.map((a) => (
                        <div key={a.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{a.email || '(sem email)'}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>Nível:</span>
                              <Select
                                value={a.adminLevel || 'padrao'}
                                onValueChange={(val) => handleSetAdminLevel(a.id, (val as 'principal' | 'padrao'))}
                              >
                                <SelectTrigger className="h-7 w-44">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="padrao">Administrador</SelectItem>
                                  <SelectItem value="principal">Administrador Principal</SelectItem>
                                </SelectContent>
                              </Select>
                              <span className="ml-4">Instituição:</span>
                              <Select
                                value={adminInstitutionByUid[a.id] ?? 'none'}
                                onValueChange={async (val) => {
                                  try {
                                    const newVal = val === 'none' ? undefined : val;
                                    await setUserInstitution(a.id, newVal);
                                    setAdminInstitutionByUid((prev) => ({ ...prev, [a.id]: newVal }));
                                    toast({ title: 'Vínculo atualizado', description: 'Instituição vinculada ao administrador.' });
                                  } catch {
                                    toast({ title: 'Falha ao vincular', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                                  }
                                }}
                              >
                                <SelectTrigger className="h-7 w-64">
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sem vínculo</SelectItem>
                                  {institutions.map((i) => (
                                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={() => handleRevokeAdmin(a.id)}>
                              Revogar
                            </Button>
                          </div>
                        </div>
                      ))}
                      {admins.length === 0 && (
                        <p className="text-sm text-muted-foreground">Nenhum administrador cadastrado.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Reconciliar Usuários do Authentication</CardTitle>
                    <CardDescription>Cria/vincula documentos em Firestore e aplica claims conforme o tipo.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="default"
                        onClick={async () => {
                          try {
                            const { adminPost } = await import('@/services/admin-api');
                            const resp = await adminPost('/reconcile-auth-users', {});
                            const data = await resp.json();
                            const s = data?.summary;
                            toast({ title: 'Reconciliação concluída', description: `Total: ${s?.total ?? 0}, docs criados: ${s?.wroteDocs ?? 0}, claims atualizados: ${s?.setClaims ?? 0}` });
                          } catch {
                            toast({ title: 'Falha na reconciliação', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                          }
                        }}
                      >
                        Reconciliar Todos
                      </Button>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            const { adminPost } = await import('@/services/admin-api');
                            const resp = await adminPost('/reconcile-auth-users', { only: 'instituicao' });
                            const data = await resp.json();
                            const s = data?.summary;
                            toast({ title: 'Reconciliação de instituições concluída', description: `Total: ${s?.total ?? 0}, docs criados: ${s?.wroteDocs ?? 0}, claims atualizados: ${s?.setClaims ?? 0}` });
                          } catch {
                            toast({ title: 'Falha na reconciliação', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                          }
                        }}
                      >
                        Reconciliar Instituições
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="apiTokens" className="space-y-6">
                <ApiTokens embedded={true} />
              </TabsContent>

              <TabsContent value="templates" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Cadastrar Modelo de Descrição</CardTitle>
                    <CardDescription>
                      Adicione modelos reutilizáveis para auxiliar os vereadores na descrição das emendas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const fd = new FormData(e.currentTarget);
                        handleAddTemplate(fd);
                        e.currentTarget.reset();
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="templateText">Texto do Modelo</Label>
                        <Input id="templateText" name="templateText" placeholder="Ex: Aquisição de equipamentos para..." required />
                        <p className="text-xs text-muted-foreground mt-1">Entre 3 e 600 caracteres.</p>
                      </div>
                      <div>
                        <Label htmlFor="templateActive">Status</Label>
                        <Select name="templateActive" defaultValue="true">
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Ativo</SelectItem>
                            <SelectItem value="false">Inativo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="w-full">
                        <i className="fas fa-plus mr-2"></i>
                        Cadastrar Modelo
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Modelos Cadastrados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {templates.map((t) => (
                        <div key={t.id} className="p-3 bg-muted rounded-lg">
                          {editingTemplateId === t.id ? (
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                handleUpdateTemplate(t.id);
                              }}
                              className="space-y-3"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                  <Label htmlFor={`tpl-text-${t.id}`}>Texto</Label>
                                  <Input
                                    id={`tpl-text-${t.id}`}
                                    value={editTemplateValues.text}
                                    onChange={(ev) => setEditTemplateValues((v) => ({ ...v, text: ev.target.value }))}
                                    required
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`tpl-active-${t.id}`}>Status</Label>
                                  <Select
                                    value={String(editTemplateValues.active)}
                                    onValueChange={(val) => setEditTemplateValues((v) => ({ ...v, active: val === 'true' }))}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="true">Ativo</SelectItem>
                                      <SelectItem value="false">Inativo</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button type="button" variant="outline" onClick={() => setEditingTemplateId(null)}>
                                  Cancelar
                                </Button>
                                <Button type="submit">Salvar</Button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                              <div>
                                <p className="font-medium">{t.text}</p>
                                <p className="text-sm text-muted-foreground">{t.active ? 'Ativo' : 'Inativo'}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setEditingTemplateId(t.id);
                                    setEditTemplateValues({ text: t.text, active: t.active });
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button
                                  variant={t.active ? 'destructive' : 'default'}
                                  onClick={async () => {
                                    try {
                                      await updateDescriptionTemplate(t.id, { active: !t.active });
                                      const updated = await listDescriptionTemplates();
                                      setTemplates(updated);
                                      toast({ title: t.active ? 'Modelo inativado' : 'Modelo ativado', description: 'Status atualizado.' });
                                    } catch {
                                      toast({ title: 'Erro ao alterar status', description: 'Tente novamente mais tarde.', variant: 'destructive' });
                                    }
                                  }}
                                >
                                  {t.active ? 'Inativar' : 'Ativar'}
                                </Button>
                                <Button
                                  variant="destructive"
                                  onClick={() => handleDeleteTemplate(t.id)}
                                >
                                  Excluir
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {templates.length === 0 && (
                        <p className="text-sm text-muted-foreground">Nenhum modelo cadastrado.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="councilors" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Cadastrar Vereador</CardTitle>
                    <CardDescription>
                      Adicione vereadores e defina seus limites de emendas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleAddCouncilor(formData);
                      e.currentTarget.reset();
                    }} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="councilorName">Nome do Vereador</Label>
                          <Input
                            id="councilorName"
                            name="councilorName"
                            placeholder="Ex: Vereador João Silva"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="party">Partido/UF</Label>
                          <Input
                            id="party"
                            name="party"
                            placeholder="Ex: PSDB-SP"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="totalLimit">Limite Total de Emendas</Label>
                          <MoneyInput
                            id="totalLimit"
                            name="totalLimit"
                            placeholder="R$ 0"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="photoUrl">Link da Foto</Label>
                          <Input
                            id="photoUrl"
                            name="photoUrl"
                            type="url"
                            placeholder="https://exemplo.com/foto.jpg"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">E-mail de Login</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="vereador@dominio.com"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="password">Senha Inicial</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="Defina uma senha forte"
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-1">A conta de acesso é criada via script administrativo.</p>
                        </div>
                      </div>
                      <Button type="submit" className="w-full">
                        <i className="fas fa-user-plus mr-2"></i>
                        Cadastrar Vereador
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Vereadores Cadastrados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {councilors.map((c) => (
                        <div key={c.id} className="p-3 bg-muted rounded-lg">
                          {editingCouncilorId === c.id ? (
                            <form
                              onSubmit={async (e) => {
                                e.preventDefault();
                                const numericLimit = parseInt(editValues.totalLimit.replace(/\D/g, '')) || 0;
                                try {
                                  await updateCouncilor(c.id, { name: editValues.name, party: editValues.party, totalLimit: numericLimit, email: editValues.email, photoUrl: editValues.photoUrl });
                                  const updated = await listCouncilors();
                                  setCouncilors(updated);
                                  setEditingCouncilorId(null);
                                  toast({ title: 'Vereador atualizado', description: `${editValues.name} salvo com sucesso.` });
                                } catch {
                                  toast({ title: 'Erro ao atualizar', description: 'Não foi possível salvar as alterações.', variant: 'destructive' });
                                }
                              }}
                              className="space-y-3"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <Label htmlFor={`name-${c.id}`}>Nome</Label>
                                  <Input
                                    id={`name-${c.id}`}
                                    value={editValues.name}
                                    onChange={(ev) => setEditValues((v) => ({ ...v, name: ev.target.value }))}
                                    required
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`party-${c.id}`}>Partido/UF</Label>
                                  <Input
                                    id={`party-${c.id}`}
                                    value={editValues.party}
                                    onChange={(ev) => setEditValues((v) => ({ ...v, party: ev.target.value }))}
                                    required
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`limit-${c.id}`}>Limite Total</Label>
                                  <MoneyInput
                                    id={`limit-${c.id}`}
                                    value={editValues.totalLimit}
                                    onChange={(val) => setEditValues((v) => ({ ...v, totalLimit: val }))}
                                    required
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <Label htmlFor={`email-${c.id}`}>E-mail</Label>
                                  <Input
                                    id={`email-${c.id}`}
                                    value={editValues.email}
                                    onChange={(ev) => setEditValues((v) => ({ ...v, email: ev.target.value }))}
                                    type="email"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`photo-${c.id}`}>Foto (URL)</Label>
                                  <Input
                                    id={`photo-${c.id}`}
                                    value={editValues.photoUrl}
                                    onChange={(ev) => setEditValues((v) => ({ ...v, photoUrl: ev.target.value }))}
                                    type="url"
                                  />
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        if (!c.email) {
                                          toast({ title: 'E-mail ausente', description: 'Defina o e-mail do vereador antes de enviar a redefinição.', variant: 'destructive' });
                                          return;
                                        }
                                        await sendPasswordResetEmail(auth, c.email);
                                        toast({ title: 'Redefinição enviada', description: `Link de redefinição enviado para ${c.email}.` });
                                      } catch {
                                        toast({ title: 'Erro ao enviar redefinição', description: 'Verifique o e-mail configurado.', variant: 'destructive' });
                                      }
                                    }}
                                  >
                                    Enviar redefinição de senha
                                  </Button>
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button type="button" variant="outline" onClick={() => setEditingCouncilorId(null)}>
                                  Cancelar
                                </Button>
                                <Button type="submit">
                                  Salvar
                                </Button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                              <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-sm text-muted-foreground">{c.party}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-bold text-accent">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.totalLimit)}
                                </span>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setEditingCouncilorId(c.id);
                                    setEditValues({ name: c.name, party: c.party, totalLimit: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.totalLimit), email: c.email || '', photoUrl: c.photoUrl || '' });
                                  }}
                                >
                                  Editar
                                </Button>
                                {isAdminUser && (
                                  <Button
                                    variant="destructive"
                                    onClick={() => {
                                      setCouncilorToDelete(c);
                                      setDeleteDialogOpen(true);
                                    }}
                                    aria-label={`Excluir vereador ${c.name}`}
                                  >
                                    Excluir
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle id="delete-councilor-title">Confirmar Exclusão</DialogTitle>
                            <DialogDescription id="delete-councilor-desc">
                              {councilorToDelete ? (
                                <span>
                                  Tem certeza que deseja excluir <strong>{councilorToDelete.name}</strong> ({councilorToDelete.party})? Esta ação não pode ser desfeita.
                                </span>
                              ) : (
                                'Selecione um vereador para excluir.'
                              )}
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setCouncilorToDelete(null); }}>Cancelar</Button>
                            <Button variant="destructive" onClick={handleConfirmDeleteCouncilor} disabled={!councilorToDelete}>Excluir</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="settings" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      <i className="fas fa-user-shield mr-2"></i>
                      Bloqueio dos Vereadores
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <i className="fas fa-info-circle ml-2 text-muted-foreground" aria-label="Ajuda"></i>
                        </TooltipTrigger>
                        <TooltipContent>Bloqueia alterações nas emendas pelos vereadores conforme agendamento.</TooltipContent>
                      </Tooltip>
                    </CardTitle>
                    <CardDescription>
                      Gerencie os prazos e bloqueios para alocação de emendas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                        <div>
                          <h4 className="font-medium text-foreground">Status das Alocações</h4>
                          <p className="text-sm text-muted-foreground">
                            {allocationsLocked ? 'Alocações bloqueadas - Vereadores não podem modificar emendas' : 'Alocações liberadas - Vereadores podem modificar emendas'}
                          </p>
                          {lockSchedule.enabled && lockSchedule.date && lockSchedule.time && (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Agendado para {formatDateTimeLabel(lockSchedule.date, lockSchedule.time)}</span>
                              {(() => {
                                const now = Date.now();
                                const start = new Date(`${lockSchedule.date}T${lockSchedule.time}:00`).getTime();
                                const end = start + 60 * 60 * 1000;
                                const future = now < start && !allocationsLocked;
                                const progress = now >= start && now <= end && !allocationsLocked;
                                const done = allocationsLocked || now > end;
                                return (
                                  <span className={
                                    `px-2 py-1 text-xs rounded ${future ? 'bg-blue-200 text-blue-900' : progress ? 'bg-amber-200 text-amber-900' : 'bg-muted text-muted-foreground'}`
                                  }>
                                    {done ? `Bloqueado em ${formatDateTimeLabel(lockSchedule.date, lockSchedule.time)}` : future ? 'Bloqueio futuro' : 'Bloqueio em andamento'}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={toggleAllocations}
                          variant={allocationsLocked ? "destructive" : "default"}
                        >
                          <i className={`fas ${allocationsLocked ? 'fa-unlock' : 'fa-lock'} mr-2`}></i>
                          {allocationsLocked ? 'Liberar Alocações' : 'Bloquear Alocações'}
                        </Button>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <form onSubmit={setInstitutionSchedule} className="space-y-4" aria-label="Agendamento de respostas das instituições">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="instStartDate">Início (data)</Label>
                              <Input id="instStartDate" name="instStartDate" type="date" defaultValue={instFormSchedule.startDate} required />
                            </div>
                            <div>
                              <Label htmlFor="instStartTime">Início (hora)</Label>
                              <Input id="instStartTime" name="instStartTime" type="time" step={60} defaultValue={instFormSchedule.startTime} required />
                            </div>
                            <div>
                              <Label htmlFor="instEndDate">Término (data)</Label>
                              <Input id="instEndDate" name="instEndDate" type="date" defaultValue={instFormSchedule.endDate} required />
                            </div>
                            <div>
                              <Label htmlFor="instEndTime">Término (hora)</Label>
                              <Input id="instEndTime" name="instEndTime" type="time" step={60} defaultValue={instFormSchedule.endTime} required />
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm">
                              <input type="checkbox" name="instNotify" defaultChecked={!!instFormSchedule.notify} /> Notificar instituições automaticamente
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input type="checkbox" name="instCompleted" defaultChecked={!!instFormSchedule.completed} /> Marcar como concluído
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const now = Date.now();
                              const has = instFormSchedule.enabled && instFormSchedule.startDate && instFormSchedule.startTime && instFormSchedule.endDate && instFormSchedule.endTime;
                              if (!has) return null;
                              const start = new Date(`${instFormSchedule.startDate}T${instFormSchedule.startTime}:00`).getTime();
                              const end = new Date(`${instFormSchedule.endDate}T${instFormSchedule.endTime}:00`).getTime();
                              const future = now < start && !instFormSchedule.completed;
                              const progress = now >= start && now <= end && !instFormSchedule.completed;
                              const done = instFormSchedule.completed || now > end;
                              const cls = future ? 'bg-blue-200 text-blue-900' : progress ? 'bg-amber-200 text-amber-900' : 'bg-muted text-muted-foreground';
                              const label = done ? `Concluído${now > end ? ` • Encerrado em ${formatDateTimeLabel(instFormSchedule.endDate, instFormSchedule.endTime)}` : ''}` : future ? 'Aguardando período' : 'Período em andamento';
                              return <span className={`px-2 py-1 text-xs rounded ${cls}`}>{label}</span>;
                            })()}
                          </div>
                          <Button type="submit" className="w-full">
                            <i className="fas fa-calendar-check mr-2"></i>
                            Definir Agendamento para Instituições
                          </Button>
                        </form>
                      </div>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">
                            <i className="fas fa-user-shield mr-2"></i>
                            Bloqueio dos Vereadores
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <i className="fas fa-info-circle ml-2 text-muted-foreground" aria-label="Ajuda"></i>
                              </TooltipTrigger>
                              <TooltipContent>Agende o bloqueio automático para impedir alterações pelos vereadores.</TooltipContent>
                            </Tooltip>
                          </CardTitle>
                          <CardDescription>
                            Programe quando o sistema deve bloquear automaticamente as alocações
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={handleScheduleLock} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="lockDate">Data do Bloqueio</Label>
                                <Input
                                  id="lockDate"
                                  name="lockDate"
                                  type="date"
                                  min={todayIsoDateCuiaba()}
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="lockTime">Horário</Label>
                                <Input
                                  id="lockTime"
                                  name="lockTime"
                                  type="time"
                                  step={60}
                                  inputMode="numeric"
                                  lang="pt-BR"
                                  required
                                />
                              </div>
                            </div>
                            <Button type="submit" className="w-full" variant="outline">
                              <i className="fas fa-calendar-check mr-2"></i>
                              Agendar Bloqueio
                            </Button>
                          </form>
                          {lockSchedule.enabled && (
                            <div className="mt-4 p-3 bg-warning/10 border border-warning rounded-lg">
                              <p className="text-sm">
                                <i className="fas fa-info-circle text-warning mr-2"></i>
                                Bloqueio agendado para: {formatDateTimeLabel(lockSchedule.date, lockSchedule.time)}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </main>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 5),
    digits.slice(5, 8),
    digits.slice(8, 12),
    digits.slice(12, 14),
  ];
  let out = '';
  if (parts[0]) out = parts[0];
  if (parts[1]) out += '.' + parts[1];
  if (parts[2]) out += '.' + parts[2];
  if (parts[3]) out += '/' + parts[3];
  if (parts[4]) out += '-' + parts[4];
  return out;
};

const validateCnpj = (cnpj: string) => {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const calc = (base: string, factors: number[]) => {
    let sum = 0;
    for (let i = 0; i < factors.length; i++) sum += parseInt(base[i]) * factors[i];
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const b12 = digits.slice(0, 12);
  const d1 = calc(b12, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const b13 = b12 + String(d1);
  const d2 = calc(b13, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return digits.endsWith(String(d1) + String(d2));
};
export const normalizeCnpj = (cnpj: string) => cnpj.replace(/\D/g, '');
export const groupInstitutions = (list: Institution[]) => {
  const map = new Map<string, Institution[]>();
  for (const i of list) {
    const key = normalizeCnpj(i.cnpj || '') || 'SEM-CNPJ';
    const arr = map.get(key) || [];
    arr.push(i);
    map.set(key, arr);
  }
  const groups = Array.from(map.entries()).map(([key, items]) => ({ key, label: key === 'SEM-CNPJ' ? 'Sem CNPJ' : formatCnpj(key), items: items.sort((a, b) => (a.name || '').localeCompare(b.name || '')) }));
  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
};
// moved into component

export type InstitutionCsvRow = {
  name: string;
  category: string;
  link: string;
  cnpj?: string;
  legalName?: string;
  contactEmail?: string;
  contactPhone?: string;
  logoUrl?: string;
  social?: { facebook?: string; instagram?: string; x?: string; tiktok?: string; youtube?: string; whatsapp?: string };
  responsibleEmail?: string;
};

export function parseInstitutionsCsv(text: string): { rows: InstitutionCsvRow[]; errors: string[]; legacy: boolean } {
  const split = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result.map((s) => s.trim());
  };

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], errors: [], legacy: false };
  const header = split(lines[0]).map((h) => h.toLowerCase());
  const nameIdx = header.findIndex((h) => h === 'nome' || h === 'name');
  const catIdx = header.findIndex((h) => h === 'categoria' || h === 'category');
  const linkIdx = header.findIndex((h) => h === 'link');
  const cnpjIdx = header.findIndex((h) => h === 'cnpj');
  const legalIdx = header.findIndex((h) => h === 'legal_name' || h === 'razao_social');
  const emailIdx = header.findIndex((h) => h === 'contact_email');
  const phoneIdx = header.findIndex((h) => h === 'contact_phone');
  const logoIdx = header.findIndex((h) => h === 'logo_url');
  const respEmailIdx = header.findIndex((h) => h === 'responsible_email');
  const fbIdx = header.findIndex((h) => h === 'social_facebook');
  const igIdx = header.findIndex((h) => h === 'social_instagram');
  const xIdx = header.findIndex((h) => h === 'social_x');
  const tkIdx = header.findIndex((h) => h === 'social_tiktok');
  const ytIdx = header.findIndex((h) => h === 'social_youtube');
  const waIdx = header.findIndex((h) => h === 'social_whatsapp');
  const required = [nameIdx, catIdx, linkIdx];
  if (required.some((i) => i === -1)) {
    throw new Error('Cabeçalho CSV inválido. Esperado ao menos: nome,categoria,link');
  }
  const legacy = cnpjIdx === -1;
  const rows: InstitutionCsvRow[] = [];
  const errors: string[] = [];
  for (let lineNum = 1; lineNum < lines.length; lineNum++) {
    const cols = split(lines[lineNum]);
    const name = cols[nameIdx]?.trim();
    const category = cols[catIdx]?.trim();
    const link = cols[linkIdx]?.trim();
    const cnpjRaw = cnpjIdx !== -1 ? (cols[cnpjIdx]?.trim() || '') : '';
    const legalName = legalIdx !== -1 ? (cols[legalIdx]?.trim() || '') : undefined;
    const contactEmail = emailIdx !== -1 ? (cols[emailIdx]?.trim() || '') : undefined;
    const contactPhone = phoneIdx !== -1 ? (cols[phoneIdx]?.trim() || '') : undefined;
    const logoUrl = logoIdx !== -1 ? (cols[logoIdx]?.trim() || '') : undefined;
    const responsibleEmail = respEmailIdx !== -1 ? (cols[respEmailIdx]?.trim() || '') : undefined;
    const social = {
      facebook: fbIdx !== -1 ? (cols[fbIdx]?.trim() || '') : undefined,
      instagram: igIdx !== -1 ? (cols[igIdx]?.trim() || '') : undefined,
      x: xIdx !== -1 ? (cols[xIdx]?.trim() || '') : undefined,
      tiktok: tkIdx !== -1 ? (cols[tkIdx]?.trim() || '') : undefined,
      youtube: ytIdx !== -1 ? (cols[ytIdx]?.trim() || '') : undefined,
      whatsapp: waIdx !== -1 ? (cols[waIdx]?.trim() || '') : undefined,
    };
    if (!name || !category || !link) continue;
    if (!legacy) {
      const formatted = formatCnpj(cnpjRaw);
      if (!validateCnpj(formatted)) {
        errors.push(`Linha ${lineNum + 1}: CNPJ inválido (${cnpjRaw})`);
        continue;
      }
      if (contactEmail && !/.+@.+\..+/.test(contactEmail)) {
        errors.push(`Linha ${lineNum + 1}: email de contato inválido (${contactEmail})`);
      }
      rows.push({ name, category, link, cnpj: formatted, legalName, contactEmail, contactPhone, logoUrl, social, responsibleEmail });
    } else {
      if (!cnpjRaw) {
        errors.push(`Linha ${lineNum + 1}: CNPJ ausente (modo legado)`);
      }
      rows.push({ name, category, link });
    }
  }
  return { rows, errors, legacy };
}